import * as tus from "tus-js-client";
import { v4 as uuidv4 } from "uuid";
import http from "./http-common";
import { getAuthToken, getCurrentAuthUser } from "./authService";
import { openUploadEventsStream } from "./UploadEventsService";
import { uploadTelemetryService } from "@/utils/uploadTelemetry";
import { captureUploadRequestErrorTelemetry } from "@/utils/uploadErrorTelemetry";
import {
  chunkSizeForConnection,
  parallelFilesForConnection,
} from "@/utils/uploadConcurrencyTuner";
import {
  fileFingerprint,
  markFileUploaded,
  markPackagePaused,
  persistFileProgress,
  removePendingPackage,
  savePendingPackage,
} from "@/utils/uploadPersistence";
import { notifyUploadCompleted } from "./uploadCompletionEvents";
import { notifyAutoPause } from "./autoPauseEvents";
import { buildAutoPauseSentinel, isAutoPauseSentinel } from "./autoPauseSentinel";
import {
  enqueuePendingCancel,
  listPendingCancels,
  prunePendingCancels,
  removePendingCancel,
} from "./pendingCancelQueue";
import { acquireUploadLock } from "@/utils/uploadOwnership";
import { getUploadSessionId, UPLOAD_SESSION_HEADER } from "@/utils/uploadSessionId";
import { classifyTusError } from "./classifyTusError";
import { notifyUploadQueueChanged } from "./uploadQueueEvents";
import {
  announceUpload,
  announceUploadTerminal,
} from "@/observability/uploadHook";
import { uploadDiagnosticsRegistry } from "@/observability";

// Tail extends past the typical macOS sleep-wake window so a laptop that suspends mid-upload
// (TUS PATCH socket dies, OS pauses fetch retries) gets the chance to reconnect after wake
// instead of falling straight through to onError — FAILED. Total budget — 12 min.
const TUS_RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 20000, 30000, 60000, 120000, 180000, 240000];
const TOKEN_REFRESH_SAFETY_MS = 5 * 60 * 1000;
const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
// Snapshot per-file bytesUploaded to IndexedDB on this cadence so a crash/reload during an
// active upload can rehydrate the progress card with the actual byte counts instead of zero.
const PROGRESS_SNAPSHOT_INTERVAL_MS = 5000;
const SESSION_EXPIRED_MESSAGE =
  "Your session expired. Please sign in again and retry the upload.";
const BACKEND_UPLOAD_READY_STATUSES = new Set([
  "READY_TO_UPLOAD_TO_PROVIDER",
  "IN_PROGRESS_UPLOAD_TO_PROVIDER",
  "READY_FOR_CAT_DV",
  "CAT_DV_INGESTED",
  "UPLOADED",
  "EDIT_IN_PROGRESS",
  "EDITED",
  "TAGGING_IN_PROGRESS",
  "TAGGED",
  "PUBLISHED",
  // Processing is paused, but the bytes are in — the browser has nothing left to wait for.
  "ON_HOLD",
]);
const BACKEND_UPLOAD_FAILED_STATUSES = new Set([
  "FAILED",
  "REJECTED",
  "CANCELLED",
]);

// /status poll back-off: when the backend replies 5xx (typical of Hikari pool saturation),
// we exponentially delay the next poll so the client fleet doesn't hammer a struggling
// server. Reset to base on the next 2xx. Network errors (offline, DNS blip) keep the
// current cadence the user is likely mid-Wi-Fi-switch and needs the fast recovery poll.
// See docs/HIKARI_POOL_EXHAUSTION_PLAN.md for the incident that motivated this.
const STATUS_POLL_BASE_MS = 15_000;
const STATUS_POLL_MAX_MS = 5 * 60_000;
const STATUS_POLL_BACKOFF_CAP = 5; // 2^5 == 32x base, then STATUS_POLL_MAX_MS clamps

const isServerErrorResponse = (err) => {
  const status = err?.response?.status;
  return typeof status === "number" && status >= 500 && status < 600;
};

const computeStatusPollDelay = (consecutive5xx) => {
  if (!consecutive5xx) return STATUS_POLL_BASE_MS;
  const multiplier = Math.pow(2, Math.min(consecutive5xx, STATUS_POLL_BACKOFF_CAP));
  return Math.min(STATUS_POLL_MAX_MS, STATUS_POLL_BASE_MS * multiplier);
};

const tokenCache = { token: null, expiresAt: 0 };
const inFlight = new Map(); // packageId -> session

// Global single-package upload queue. Two packages uploading in parallel produced broken-pipe
// errors on the BE: the browser's HTTP/1.1 connection budget + the BE's per-PATCH @Transactional
// file assembly ended up timing out one package's chunks while the other finalized. We serialize
// at the package level so the active package gets the full bandwidth and connection budget,
// while queued packages wait with their TUS sessions already created server-side.
let activePackageId = null;
const queueWaiters = []; // [{ uploadId, resolve }]
// Slots reserved by rehydrated paused packages (boot-time IndexedDB rehydration). They aren't
// in-flight yet the user still needs to provide files via the picker but conceptually they
// hold the head of the queue. Without this, a new upload added after a reload would race past
// the rehydrated paused one and start uploading immediately, contradicting the user's "queued
// behind the older paused one" expectation.
const reservedSlotIds = new Set();

export const reserveSlotForRehydrated = (uploadId) => {
  if (!uploadId) return;
  reservedSlotIds.add(uploadId);
};

export const releaseReservedSlot = (uploadId) => {
  if (!uploadId) return;
  const hadReservation = reservedSlotIds.delete(uploadId);
  // Cancelling a rehydrated upload that never resumed (user hit Cancel on the "needs folder"
  // card without picking files) hits ONLY this path — there is no inFlight session, so
  // session.abort() → releaseUploadSlot() never runs, and any follow-up upload queued behind
  // this reservation would sit in "Waiting for the previous upload to finish" forever.
  if (hadReservation && !activePackageId) {
    promoteNextEligibleWaiter();
  }
};

const hasReservationAhead = (uploadId) =>
  // Any reservation other than this upload's own counts as "ahead". A package's own reservation
  // doesn't block its own acquisition claiming the slot consumes it.
  Array.from(reservedSlotIds).some((id) => id !== uploadId);

// Emit whenever the queue snapshot changes so the UI can render queued
// uploads with the hourglass icon reliably (bug 2026-08-05). Called from
// every mutation of activePackageId / queueWaiters below.
const emitQueueSnapshot = () => {
  notifyUploadQueueChanged({
    activeUploadId: activePackageId,
    queuedUploadIds: queueWaiters.map((w) => w.uploadId),
  });
};

// Pick the first queued waiter whose reservation situation lets it start, promote it to
// active, and resolve its slot promise. Idempotent and callable from any path that clears a
// blocker (active-release or reservation-release). Returns whether a promotion happened so
// callers can decide whether they still need to emit an "empty slot" snapshot.
const promoteNextEligibleWaiter = () => {
  if (activePackageId) return false;
  for (let i = 0; i < queueWaiters.length; i++) {
    const candidate = queueWaiters[i];
    if (!hasReservationAhead(candidate.uploadId)) {
      const [waiter] = queueWaiters.splice(i, 1);
      activePackageId = waiter.uploadId;
      waiter.resolve();
      emitQueueSnapshot();
      return true;
    }
  }
  return false;
};

const acquireUploadSlot = (uploadId) => {
  // Consume this upload's own reservation (if any) the resume path calls acquire with the
  // same uploadId that boot rehydration registered. A package that HAD a reservation is a
  // rehydrated package being resumed; it already holds a head-of-queue position and must not
  // be blocked by sibling rehydrated reservations (which exist only to block fresh uploads).
  // Without this carve-out, two paused packages rehydrated after a reload would race: clicking
  // Resume on the older one queues it behind the younger one's reservation, then the auto-chain
  // resume of the younger one finds an empty queue and grabs the slot so the wrong package
  // uploads first.
  const wasReserved = reservedSlotIds.has(uploadId);
  reservedSlotIds.delete(uploadId);
  if (!activePackageId && (wasReserved || !hasReservationAhead(uploadId))) {
    activePackageId = uploadId;
    emitQueueSnapshot();
    return Promise.resolve(false);
  }
  const promise = new Promise((resolve) => {
    queueWaiters.push({ uploadId, resolve });
  }).then(() => true);
  emitQueueSnapshot();
  return promise;
};

// Idempotent: called by the active package when its bytes are done, and by abort() for either
// the active OR a queued package (cancel-from-queue). For an active release we promote the next
// waiter; for a queued release we just drop it from the queue.
const releaseUploadSlot = (uploadId) => {
  if (activePackageId === uploadId) {
    activePackageId = null;
    if (!promoteNextEligibleWaiter()) {
      // Nobody to promote (queue empty or all waiters blocked by a reservation). Still emit
      // so the UI reflects the slot going idle.
      emitQueueSnapshot();
    }
    return;
  }
  const idx = queueWaiters.findIndex((w) => w.uploadId === uploadId);
  if (idx >= 0) {
    const [waiter] = queueWaiters.splice(idx, 1);
    waiter.resolve();
    emitQueueSnapshot();
  }
};

// Custom tus-js-client fingerprint function: scopes URL storage by (packageUploadId, sourceFileId)
// so two simultaneous uploads of the same physical file (different chrome windows / incognito /
// queued sequentially in the same browser) get independent localStorage entries. The library's
// default fingerprint is just name|size|type|lastModified, which collides whenever the same file
// is uploaded twice findPreviousUploads then cross-pollinates URLs between packages and the
// "wrong" package gets a fake onSuccess for a file the server never saw a single byte of from
// it. See the comments at the tus.Upload constructor sites for the failure mode in detail.
//
// We also include name|size|lastModified to keep the existing tus-js-client recovery semantics
// for the legitimate "same package resumes after reload" path: a different file substituted into
// the same slot (different name/size/lastModified) gets a different fingerprint and starts fresh.
const makeNamespacedFingerprint = (packageUploadId, sourceFileId) => (file) =>
  Promise.resolve(
    `clipn|pkg=${packageUploadId}|sf=${sourceFileId}|name=${file.name}|size=${file.size}|mtime=${file.lastModified || 0}`
  );

// Resolve a server-supplied TUS URL to an absolute one so tus-js-client HEAD/PATCHes hit the
// backend origin, not the FE dev server. Server returns paths like `/api/v1/upload/tus/{id}`,
// which resolve against `window.location` unless we prefix them. Kept a plain string join
// rather than `new URL(url, base)` to preserve the existing shape (no trailing slashes / no
// query munging).
const toAbsoluteTusUrl = (baseUrl, url) => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  if (!baseUrl) return url;
  return `${baseUrl.replace(/\/$/, "")}${url}`;
};

// Per-package per-file progress snapshot for the UI to render rows beyond just an aggregate.
// Shape: Map<uploadId, Map<sourceFileId, { originalName, sizeBytes, loaded, status, retries, error }>>
const perFileProgress = new Map();
const perFileListeners = new Map();

const ensurePackage = (uploadId) => {
  if (!perFileProgress.has(uploadId)) {
    perFileProgress.set(uploadId, new Map());
  }
  return perFileProgress.get(uploadId);
};

const notifyPerFile = (uploadId) => {
  const subs = perFileListeners.get(uploadId);
  if (!subs) return;
  const snapshot = Array.from((perFileProgress.get(uploadId) || new Map()).values());
  subs.forEach((listener) => {
    try { listener(snapshot); } catch {}
  });
};

const updatePerFile = (uploadId, sourceFileId, patch) => {
  const pkg = ensurePackage(uploadId);
  const prev = pkg.get(sourceFileId) || {};
  pkg.set(sourceFileId, { ...prev, ...patch });
  notifyPerFile(uploadId);
};

export const subscribePerFileProgress = (uploadId, listener) => {
  if (!uploadId || typeof listener !== "function") return () => {};
  const subs = perFileListeners.get(uploadId) || new Set();
  subs.add(listener);
  perFileListeners.set(uploadId, subs);
  const snapshot = Array.from((perFileProgress.get(uploadId) || new Map()).values());
  if (snapshot.length > 0) listener(snapshot);
  return () => {
    const current = perFileListeners.get(uploadId);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) perFileListeners.delete(uploadId);
  };
};

export const getPerFileProgress = (uploadId) =>
  Array.from((perFileProgress.get(uploadId) || new Map()).values());

const clearPerFile = (uploadId) => {
  perFileProgress.delete(uploadId);
  const subs = perFileListeners.get(uploadId);
  if (subs) subs.forEach((listener) => { try { listener([]); } catch {} });
};

// === Stall watchdog ===
// tus.onProgress fires when bytes flush from the browser to the socket. On a healthy upload
// it fires many times per second. A prolonged silence means one of two things: (a) the browser
// finished sending the chunk body and is waiting on the server's PATCH response — normal,
// usually <2s but up to ~30s on the tail chunk of a huge file while the server finalises;
// (b) the underlying TCP has silently died (NAT dropped the mapping, dead proxy connection)
// and the kernel is retransmitting into a black hole. tus-js-client's retry never fires in
// case (b) because the fetch itself never errors — without this watchdog the user sits for
// many minutes on a frozen progress bar while the browser insists it is uploading.
//
// Progressive check cadence — 5s hint, 15s stalled banner, 30s abort+restart:
// - 5s: subtle "reconnecting…" hint. Passes through most cross-chunk waits (<2s) and even
//   large-file finalisation windows without noise, but surfaces a signal quickly if things
//   actually are wrong.
// - 15s: "Connection stalled" banner + surfaces the "Retry now" button.
// - 30s: abort the current PATCH and restart the tus.Upload. Safe even mid-finalisation:
//   tus.abort(false) tears down the current PATCH without deleting the server-side resource;
//   tus.start() re-HEADs to fetch the current offset. Worst case (abort races the server's
//   finalisation): a few seconds of "Unable to lock upload" while the server holds the file
//   lock, then HEAD returns offset==length and onSuccess fires. No data loss.
//
// Restart cap: without a limit a hopeless connection would loop the watchdog forever every
// 30s. After WATCHDOG_MAX_RESTARTS_PER_FILE hits we stop firing and let tus's own retry
// budget (TUS_RETRY_DELAYS) take over surfacing the failure through the normal path.
const WATCHDOG_HINT_MS = 5_000;
const WATCHDOG_STALLED_MS = 15_000;
const WATCHDOG_ABORT_MS = 30_000;
const WATCHDOG_POLL_MS = 2_000;
const WATCHDOG_MAX_RESTARTS_PER_FILE = 5;

// Best-effort POST to the server's stall-telemetry endpoint. Fire-and-forget: NEVER awaited by
// the watchdog, and never surfaces errors to the user. A failing telemetry POST during the
// very stall we're reporting (server unreachable, request queued behind the stuck PATCH, etc.)
// is expected and non-actionable — we only log at INFO so the client-side signal stays greppable
// in browser devtools without polluting the error path.
const postStallTelemetry = (payload) => {
  refreshTokenIfNeeded()
    .then((token) => http.post('/api/v1/upload/telemetry/stall', payload, {
      headers: { Authorization: `Bearer ${token}` },
      // Short timeout so a stall-reporting POST that is itself stalling can't accumulate.
      timeout: 5_000,
    }))
    .catch((err) => {
      console.info('[UPLOAD-DIAG] stall-telemetry/post-failed', {
        uploadId: payload?.uploadId,
        source: payload?.source,
        status: err?.response?.status,
        message: err?.message,
      });
    });
};

const buildStallTelemetryPayload = ({ uploadId, sourceFileId, filename, source, restarts, stalledMs }) => {
  const conn = (typeof navigator !== 'undefined' && navigator.connection) || {};
  const pkg = perFileProgress.get(uploadId);
  const entry = pkg ? pkg.get(sourceFileId) : null;
  const telemetryState = uploadTelemetryService.getState?.(uploadId) || null;
  return {
    uploadId,
    sourceFileId,
    filename,
    source,
    restartCount: restarts,
    stalledMs,
    bytesUploaded: entry?.loaded ?? 0,
    totalBytes: entry?.sizeBytes ?? 0,
    rollingBps: typeof telemetryState?.rollingBps === 'number' ? telemetryState.rollingBps : null,
    connection: {
      effectiveType: typeof conn.effectiveType === 'string' ? conn.effectiveType : null,
      downlink: typeof conn.downlink === 'number' ? conn.downlink : null,
      rtt: typeof conn.rtt === 'number' ? conn.rtt : null,
      saveData: typeof conn.saveData === 'boolean' ? conn.saveData : null,
    },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    sessionId: getUploadSessionId(),
    timestamp: new Date().toISOString(),
  };
};

const createStallWatchdog = ({ uploadId, sourceFileId, sessionRecord, filename }) => {
  let stopped = false;
  let restarts = 0;
  let tusRef = null;
  let lastProgressMs = Date.now();
  let currentState = null; // null | 'hint' | 'stalled' | 'reconnecting'
  let pollTimer = null;
  // Timestamp of the first transition into 'stalled' for the current stall episode. Reset to
  // null when notifyProgress() clears the state. Feeds the telemetry POST's `stalledMs` so
  // the server can distinguish "brief hiccup" from "user watched a frozen bar for a minute".
  let firstStalledAtMs = null;

  const applyState = (state) => {
    if (currentState === state) return;
    if (state === 'stalled' && firstStalledAtMs === null) firstStalledAtMs = Date.now();
    if (state === null) firstStalledAtMs = null;
    currentState = state;
    updatePerFile(uploadId, sourceFileId, { stallState: state });
  };

  const emitTelemetry = (source) => {
    // Fire-and-forget: never awaited. A failing telemetry POST during the stall itself (the
    // very thing that made us stall may also make the POST fail) must never gate the actual
    // abort/restart. postStallTelemetry logs its own failure so it stays greppable.
    const payload = buildStallTelemetryPayload({
      uploadId, sourceFileId, filename, source, restarts,
      stalledMs: firstStalledAtMs ? Date.now() - firstStalledAtMs : 0,
    });
    postStallTelemetry(payload);
  };

  const triggerAbortRestart = () => {
    if (stopped || sessionRecord.paused || sessionRecord.cancelled) return;
    if (restarts >= WATCHDOG_MAX_RESTARTS_PER_FILE) {
      console.warn('[UPLOAD-DIAG] watchdog/give-up', {
        uploadId, sourceFileId, filename, restarts,
      });
      emitTelemetry('give-up');
      // Leave the "stalled" banner up so the user can see recovery is still being attempted.
      // tus's own onError/onShouldRetry loop still owns the terminal failure path.
      return;
    }
    restarts += 1;
    console.info('[UPLOAD-DIAG] watchdog/abort-restart', {
      uploadId, sourceFileId, filename, restarts,
    });
    // Only the auto-abort path remains — the manual "Retry now" variant was removed 2026-08
    // (users found it confusing on the bad networks where they'd reach for it). Server-side
    // alerts trigger on 'abort' after >=3 restarts on the same upload.
    emitTelemetry('abort');
    applyState('reconnecting');
    // Reset the progress clock so a HEAD taking a few seconds during the restart doesn't
    // immediately re-fire the watchdog while tus is legitimately re-hydrating the offset.
    lastProgressMs = Date.now();
    try { tusRef?.abort(false); } catch (err) {
      console.warn('[UPLOAD-DIAG] watchdog/abort-error', { uploadId, sourceFileId, message: err?.message });
    }
    try { tusRef?.start(); } catch (err) {
      console.warn('[UPLOAD-DIAG] watchdog/start-error', { uploadId, sourceFileId, message: err?.message });
    }
  };

  const tick = () => {
    if (stopped) return;
    if (sessionRecord.paused || sessionRecord.cancelled) {
      applyState(null);
      // Keep the progress clock fresh while paused — otherwise the first tick
      // after a >=30s pause sees a huge `elapsed` and fires a spurious
      // abort+restart (burning the restart budget) before any byte has had a
      // chance to move.
      lastProgressMs = Date.now();
      pollTimer = setTimeout(tick, WATCHDOG_POLL_MS);
      return;
    }
    const elapsed = Date.now() - lastProgressMs;
    if (elapsed >= WATCHDOG_ABORT_MS) {
      triggerAbortRestart();
    } else if (elapsed >= WATCHDOG_STALLED_MS) {
      applyState('stalled');
    } else if (elapsed >= WATCHDOG_HINT_MS) {
      applyState('hint');
    }
    pollTimer = setTimeout(tick, WATCHDOG_POLL_MS);
  };

  return {
    attach: (tusUpload) => {
      tusRef = tusUpload;
      lastProgressMs = Date.now();
      if (!pollTimer) pollTimer = setTimeout(tick, WATCHDOG_POLL_MS);
    },
    notifyProgress: () => {
      if (stopped) return;
      lastProgressMs = Date.now();
      // Real bytes moved, so any earlier restarts evidently recovered the
      // connection — refill the budget. The cap only needs to stop hopeless
      // loops where restarts never yield progress; without this reset a few
      // benign stall episodes over a multi-hour upload exhaust the budget and
      // a later real stall gets no recovery at all.
      restarts = 0;
      if (currentState !== null) applyState(null);
    },
    stop: () => {
      if (stopped) return;
      stopped = true;
      if (pollTimer) clearTimeout(pollTimer);
      pollTimer = null;
      applyState(null);
    },
  };
};

// Statuses we treat as truly terminal at these points the backend will not change the
// package any further, so we can safely delete IndexedDB / close the SSE / detach from inFlight.
const BACKEND_TRULY_TERMINAL_STATUSES = new Set([
  "PUBLISHED",
  "UPLOADED",
  "CAT_DV_INGESTED",
  // Terminal for the *uploader*, like CAT_DV_INGESTED above: the package will change
  // again when someone resumes it, but nothing the browser is holding open matters by
  // then, and a hold can last days — keeping IDB and the SSE alive for it leaks.
  "ON_HOLD",
  "FAILED",
  "REJECTED",
  "CANCELLED",
]);

// `waitForBackendUploadReady` returns when the backend reaches a ready status - the point
// where server-side assembly is complete and the user-visible upload can transition from
// FINALIZING to COMPLETED. The queue slot is released before this wait starts, so later
// packages are not blocked while the backend continues provider / CatDV bookkeeping.
//
// IndexedDB lifecycle is also tied to truly-terminal status here, NOT to bytes-done. If the
// backend hangs in CHUNK_UPLOAD_IN_PROGRESS / IN_PROGRESS_UPLOAD_TO_PROVIDER the persisted
// record stays alive so a page reload can re-surface the upload (see ResumePreviousUploadBanner).
const waitForBackendUploadReady = ({ uploadId, sessionRecord, onErrorLogPrefix, onTerminal }) =>
  new Promise((resolve, reject) => {
    let settled = false;
    let watcherClosed = false;
    let pollTimer = null;

    const closeWatcher = () => {
      if (watcherClosed) return;
      watcherClosed = true;
      try { stream?.close(); } catch {}
      if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
    };

    const handleStatus = (status) => {
      if (!status || watcherClosed) return;
      const isFailed = BACKEND_UPLOAD_FAILED_STATUSES.has(status);
      const isReady = BACKEND_UPLOAD_READY_STATUSES.has(status);
      const isTrulyTerminal = BACKEND_TRULY_TERMINAL_STATUSES.has(status);

      if (isFailed) {
        uploadTelemetryService.fail(uploadId);
        removePendingPackage(uploadId).catch(() => {});
        if (!settled) {
          settled = true;
          const error = new Error(`Backend reported upload status ${status}`);
          error.code = "BACKEND_UPLOAD_FAILED";
          error.status = status;
          reject(error);
        }
        if (typeof onTerminal === "function") onTerminal();
        closeWatcher();
        return;
      }

      if (isReady && !settled) {
        settled = true;
        notifyUploadCompleted({ uploadId, status });
        resolve(status);
      }

      if (isTrulyTerminal) {
        // PUBLISHED / UPLOADED / CAT_DV_INGESTED final good state. Safe to drop the
        // persisted record and detach from inFlight.
        removePendingPackage(uploadId).catch(() => {});
        if (typeof onTerminal === "function") onTerminal();
        closeWatcher();
      }
    };

    // Snapshot the backend status BEFORE relying on the SSE stream. If a network blip
    // (WiFi hotspot WiFi switch) drops the SSE around the moment the package
    // transitions to a ready status, the reconnected SSE may or may not redeliver the
    // event before its 90s stall window kicks polling in. A direct GET here closes that
    // gap if the upload is already past the byte-transfer stage, we resolve in one
    // HTTP round-trip instead of waiting on an event that already fired. We also keep
    // polling past `settled` so a late FAILED arriving while the SSE is reconnecting
    // still reaches handleStatus.
    let pollConsecutive5xx = 0;
    const pollSnapshot = (attempt = 0) => {
      if (watcherClosed) return;
      refreshTokenIfNeeded()
        .then((token) => http.get("/api/v1/metadata/status", {
          headers: { Authorization: `Bearer ${token}` },
          params: { uploadId },
        }))
        .then((response) => {
          if (watcherClosed) return;
          pollConsecutive5xx = 0;
          const status = response?.data?.originalStatus || response?.data?.status;
          handleStatus(status);
          if (!watcherClosed) {
            pollTimer = setTimeout(() => pollSnapshot(attempt + 1), STATUS_POLL_BASE_MS);
          }
        })
        .catch((err) => {
          if (watcherClosed) return;
          let nextDelay = STATUS_POLL_BASE_MS;
          if (isServerErrorResponse(err)) {
            pollConsecutive5xx += 1;
            nextDelay = computeStatusPollDelay(pollConsecutive5xx);
          }
          console.warn("[ChunkedUploadService] status snapshot failed", {
            uploadId,
            attempt,
            message: err?.message,
            status: err?.response?.status,
            consecutive5xx: pollConsecutive5xx,
            nextDelayMs: nextDelay,
          });
          pollTimer = setTimeout(() => pollSnapshot(attempt + 1), nextDelay);
        });
    };

    const stream = openUploadEventsStream({
      uploadId,
      onEvent: (payload) => {
        const status = payload?.status || payload?.originalStatus;
        handleStatus(status);
      },
      onError: (err) => {
        console.warn(onErrorLogPrefix, {
          uploadId,
          message: err?.message,
        });
      },
    });
    sessionRecord.eventsStream = { close: closeWatcher };
    pollSnapshot();
  });

// Standalone watcher used by post-reload rehydration for packages where every byte has been
// uploaded but the backend's final status isn't yet known (e.g., the previous tab was closed
// during S3 upload / CatDV ingest). Caller receives status callbacks until a truly-terminal
// status is observed. No queue slot is held here bytes are already server-side, so a new
// upload should be able to start immediately rather than queueing behind this watcher.
export const watchPackageBackendStatus = ({
  uploadId,
  onReady,
  onFailure,
  onTerminal,
}) => {
  let closed = false;
  let pollTimer = null;
  let alreadyReady = false;
  let alreadyTerminal = false;
  let stream = null;

  const close = () => {
    if (closed) return;
    closed = true;
    try { stream?.close(); } catch {}
    if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
  };

  const handleStatus = (status) => {
    if (!status || closed) return;
    if (BACKEND_UPLOAD_FAILED_STATUSES.has(status)) {
      if (!alreadyTerminal) {
        alreadyTerminal = true;
        if (typeof onFailure === "function") {
          try { onFailure(status); } catch (e) {
            console.warn("[ChunkedUploadService] watch onFailure threw", { uploadId, message: e?.message });
          }
        }
      }
      close();
      return;
    }
    if (BACKEND_UPLOAD_READY_STATUSES.has(status) && !alreadyReady) {
      alreadyReady = true;
      if (typeof onReady === "function") {
        try { onReady(status); } catch (e) {
          console.warn("[ChunkedUploadService] watch onReady threw", { uploadId, message: e?.message });
        }
      }
    }
    if (BACKEND_TRULY_TERMINAL_STATUSES.has(status) && !alreadyTerminal) {
      alreadyTerminal = true;
      if (typeof onTerminal === "function") {
        try { onTerminal(status); } catch (e) {
          console.warn("[ChunkedUploadService] watch onTerminal threw", { uploadId, message: e?.message });
        }
      }
      close();
    }
  };

  let watchConsecutive5xx = 0;
  const poll = (attempt = 0) => {
    if (closed) return;
    refreshTokenIfNeeded()
      .then((token) => http.get("/api/v1/metadata/status", {
        headers: { Authorization: `Bearer ${token}` },
        params: { uploadId },
      }))
      .then((response) => {
        if (closed) return;
        watchConsecutive5xx = 0;
        const status = response?.data?.originalStatus || response?.data?.status;
        handleStatus(status);
        if (!closed) {
          pollTimer = setTimeout(() => poll(attempt + 1), STATUS_POLL_BASE_MS);
        }
      })
      .catch((err) => {
        if (closed) return;
        let nextDelay = STATUS_POLL_BASE_MS;
        if (isServerErrorResponse(err)) {
          watchConsecutive5xx += 1;
          nextDelay = computeStatusPollDelay(watchConsecutive5xx);
        }
        console.warn("[ChunkedUploadService] watch poll failed", {
          uploadId,
          attempt,
          message: err?.message,
          status: err?.response?.status,
          consecutive5xx: watchConsecutive5xx,
          nextDelayMs: nextDelay,
        });
        pollTimer = setTimeout(() => poll(attempt + 1), nextDelay);
      });
  };

  stream = openUploadEventsStream({
    uploadId,
    onEvent: (payload) => {
      const status = payload?.status || payload?.originalStatus;
      handleStatus(status);
    },
    onError: (err) => {
      console.warn("[ChunkedUploadService] watch SSE error", { uploadId, message: err?.message });
    },
  });
  poll();

  return { close };
};

// Online/offline handling: pause TUS uploads when the browser goes offline, restart from last
// acknowledged byte when it comes back. Registered once at module load. User-paused sessions
// are skipped otherwise an offline blip would silently resume a paused upload on `online`.
if (typeof window !== "undefined" && !window.__clippnTusOnlineHookInstalled) {
  window.__clippnTusOnlineHookInstalled = true;
  window.addEventListener("offline", () => {
    inFlight.forEach((session) => {
      if (session.paused) return;
      session.tusByFile.forEach((upload) => {
        try { upload.abort(false); } catch {}
      });
    });
  });
  window.addEventListener("online", () => {
    inFlight.forEach((session) => {
      if (session.paused) return;
      session.tusByFile.forEach((upload) => {
        try { upload.start(); } catch {}
      });
    });
  });

  // macOS sleep doesn't reliably emit `offline` before suspending the JS event loop
  // the tab is hidden, fetch sockets die quietly, and on wake the browser may not
  // dispatch `online` either (the network was technically up the whole time from the
  // OS's perspective). Use visibilitychange + pageshow as belt-and-braces: when the
  // tab becomes visible again, nudge every in-flight tus upload to .start() (no-op if
  // already running; resumes from the last acknowledged byte if the socket died).
  let lastHiddenAt = 0;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      lastHiddenAt = Date.now();
      return;
    }
    // Only kick if we were hidden long enough for a sleep-style suspension. Short
    // tab-switches don't kill sockets and shouldn't trigger an extra .start() round.
    const hiddenMs = lastHiddenAt ? Date.now() - lastHiddenAt : 0;
    if (hiddenMs < 30_000) return;
    inFlight.forEach((session) => {
      if (session.paused) return;
      session.tusByFile.forEach((upload) => {
        try { upload.start(); } catch {}
      });
    });
  });
  // bfcache restore (Safari/Firefox primarily). Same rationale as visibilitychange:
  // sockets are gone, tus needs a nudge.
  window.addEventListener("pageshow", (e) => {
    if (!e.persisted) return;
    inFlight.forEach((session) => {
      if (session.paused) return;
      session.tusByFile.forEach((upload) => {
        try { upload.start(); } catch {}
      });
    });
  });
}

const decodeJwtPayload = (token) => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payloadBase64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payloadBase64 + "=".repeat((4 - (payloadBase64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (error) {
    console.warn("[ChunkedUploadService] Unable to decode token payload", {
      message: error?.message,
    });
    return null;
  }
};

const refreshTokenIfNeeded = async ({ force = false } = {}) => {
  const now = Date.now();
  if (
    !force &&
    tokenCache.token &&
    tokenCache.expiresAt - now > TOKEN_REFRESH_SAFETY_MS
  ) {
    return tokenCache.token;
  }
  const fresh = await getAuthToken({ forceRefresh: force });
  const payload = decodeJwtPayload(fresh);
  const expSeconds = Number(payload?.exp);
  tokenCache.token = fresh;
  tokenCache.expiresAt = Number.isFinite(expSeconds)
    ? expSeconds * 1000
    : now + 60 * 60 * 1000;
  return fresh;
};

const buildAuthError = (status, originalError) => {
  const wrapped = new Error(SESSION_EXPIRED_MESSAGE);
  wrapped.code = "AUTH_401";
  wrapped.userMessage = SESSION_EXPIRED_MESSAGE;
  wrapped.status = status;
  wrapped.originalError = originalError;
  return wrapped;
};

const buildUploadFailedError = (fileName, originalError) => {
  const message = originalError?.message || String(originalError);
  const wrapped = new Error(`Upload failed for "${fileName}": ${message}`);
  wrapped.code = "TUS_UPLOAD_FAILED";
  wrapped.cause = originalError;
  return wrapped;
};

// Pure decision for how launchWorker's outer terminalError branch should finalize.
// Extracted so the pass-through matrix (cross-device takeover, auto-pause on
// transient tus error) is unit-testable without mocking the surrounding tus /
// worker scaffolding.
//
// passthrough=true → throw error as-is. Caller MUST NOT call
//   uploadTelemetryService.fail() or captureUploadRequestErrorTelemetry —
//   these codes represent non-failure terminations where downstream Redux
//   state is dispatched via a different path (spectator flip for
//   UPLOAD_TAKEN_OVER, PAUSED/AUTO for AUTO_PAUSED_ON_ERROR).
// passthrough=false → caller should mark telemetry failed and fire any
//   diagnostic telemetry it wants, then throw the returned error.
export const decideTerminalErrorDisposition = (terminalError, failingFile) => {
  if (terminalError?.code === "UPLOAD_TAKEN_OVER") {
    return { passthrough: true, error: terminalError };
  }
  if (terminalError?.code === "AUTO_PAUSED_ON_ERROR") {
    return { passthrough: true, error: terminalError };
  }
  const status = terminalError?.originalResponse?.getStatus?.();
  if (status === 401) {
    return { passthrough: false, error: buildAuthError(401, terminalError) };
  }
  return {
    passthrough: false,
    error: buildUploadFailedError(failingFile, terminalError),
  };
};

/**
 * Shared file-worker pool for `upload()` and `resume()` (auto-pause fix, 2026-09).
 *
 * `runOne(pair)` uploads one file and resolves on success, resolves with an
 * auto-pause sentinel (see autoPauseSentinel.js) when tus gave up on a
 * transient error, or rejects on a terminal failure.
 *
 * Auto-pause contract: the pair is requeued and the worker loops back to the
 * resume gate instead of dying. Previously the transient branch rejected,
 * `terminalError` was set, every worker exited and `t.abort(true)` sent a
 * DELETE for each file's TUS upload. Resume then had no worker to wake, the
 * second auto-pause never reached Redux, and the partial bytes were gone
 * server-side (byte-0 restart on the next attempt).
 *
 * Terminal failures abort siblings with `abort(false)`: the bytes stay on
 * the server so a later resume (or the backend sweeper) decides their fate.
 * DELETE is reserved for an explicit cancel (`sessionRecord.abort`).
 *
 * Returns `{ terminalError, completedCount, failingFileName }`.
 */
export const runFileWorkerPool = async ({
  pairs,
  maxConcurrentFiles,
  runOne,
  sessionRecord,
  tusByFile,
}) => {
  const requeued = [];
  let nextIndex = 0;
  let completedCount = 0;
  let terminalError = null;
  let failingPair = null;

  const takeNextPair = () => {
    if (requeued.length > 0) return requeued.shift();
    if (nextIndex < pairs.length) return pairs[nextIndex++];
    return null;
  };

  const worker = async () => {
    while (true) {
      if (terminalError || sessionRecord.cancelled) return;
      // Hold the worker on the resume gate while the package is paused (user click or
      // auto-pause). Without this, pause only halts in-flight files; the queue would
      // immediately start the next pair behind the user's back.
      if (sessionRecord.paused && sessionRecord.resumeGate) {
        await sessionRecord.resumeGate;
      }
      if (terminalError || sessionRecord.cancelled) return;
      const pair = takeNextPair();
      if (!pair) return;
      try {
        const result = await runOne(pair);
        if (isAutoPauseSentinel(result)) {
          // Back of the queue; the loop top parks on the resume gate. A fresh tus.Upload
          // is built for it on resume so the retry budget and socket state start clean.
          requeued.push(result.pair || pair);
          continue;
        }
        completedCount++;
      } catch (err) {
        if (!terminalError) {
          terminalError = err;
          failingPair = pair;
          // Stop siblings so they don't keep retrying. abort(false): keep server bytes.
          for (const t of tusByFile.values()) {
            try { t.abort(false); } catch {}
          }
        }
        return;
      }
    }
  };

  const workers = Array.from(
    { length: Math.max(1, Math.min(maxConcurrentFiles, pairs.length)) },
    () => worker()
  );
  await Promise.all(workers);
  return {
    terminalError,
    completedCount,
    failingFileName: failingPair?.file?.name || "file",
  };
};

const buildOwnedByOtherTabError = (uploadId) => {
  const wrapped = new Error("Upload is already running in another tab.");
  wrapped.code = "UPLOAD_OWNED_BY_OTHER_TAB";
  wrapped.uploadId = uploadId;
  wrapped.userMessage = "Upload is already running in another tab.";
  return wrapped;
};

const upload = async (formData, onProgress) => {
  const user = getCurrentAuthUser();
  if (!user) {
    const error = new Error("Sign-in required before starting upload.");
    error.code = "AUTH_REQUIRED";
    error.userMessage = "Please sign in and retry the upload.";
    throw error;
  }

  if (!formData?.files || formData.files.length === 0) {
    throw new Error("No files provided for upload.");
  }

  const providedUploadId = formData.uploadMetadata?.uploadId;
  const resolvedUploadId = providedUploadId || uuidv4();

  // Reserve queue position SYNCHRONOUSLY before any await. Without this, two resumes dispatched
  // back-to-back (e.g. the auto-chain after a page reload) could swap their queue order based
  // on which token-refresh / init call happens to finish first the user would click Resume on
  // package A and watch package B start uploading instead. Reserving here locks in the order
  // by dispatch order. The release in the early catch covers any throw before the inner try.
  const slotPromise = acquireUploadSlot(resolvedUploadId);

  try {
    await refreshTokenIfNeeded({ force: true });
  } catch (err) {
    releaseUploadSlot(resolvedUploadId);
    throw err;
  }
  const uploadMetadata = {
    ...formData.uploadMetadata,
    uploadId: resolvedUploadId,
    startUploadTime: new Date().toISOString(),
  };

  const filesManifest = formData.files.map((file) => ({
    originalName: file.name,
    sizeBytes: file.size,
    contentType: file.type || null,
  }));
  const totalSize = filesManifest.reduce((acc, f) => acc + (f.sizeBytes || 0), 0);

  // Observability: announce the upload to the diagnostics registry the moment
  // the uploadId is minted. `subscribePerFileProgress` is exported from this
  // module — pass a thin adapter so the bridge doesn't need to import from
  // here (which would create a cycle). Every downstream event flows via the
  // uploadTelemetryService subscription and this per-file subscription.
  try {
    announceUpload(resolvedUploadId, {
      uploader: "chunked",
      fileCount: filesManifest.length,
      totalSize,
      mimeTypes: Array.from(
        new Set(filesManifest.map((f) => f.contentType).filter(Boolean))
      ),
      packageName: uploadMetadata.packageName,
      uploadSessionId: getUploadSessionId(),
      chunkedUploadService: { subscribePerFileProgress },
    });
  } catch (_error) {
    // Observability must never break the upload — swallow and continue.
  }

  const initRequestBody = {
    uploadId: uploadMetadata.uploadId,
    packageName: uploadMetadata.packageName,
    description: uploadMetadata.description,
    countryCode: uploadMetadata.countryCode,
    country: uploadMetadata.country,
    state: uploadMetadata.state,
    city: uploadMetadata.city,
    dateShot: uploadMetadata.dateShot,
    notes: uploadMetadata.notes,
    embargo: uploadMetadata.embargo,
    affiliate: uploadMetadata.affiliate,
    otherContributorId: uploadMetadata.otherContributorId,
    contributorName: uploadMetadata.contributorName,
    ccEmails: uploadMetadata.ccEmails,
    startUploadTime: uploadMetadata.startUploadTime,
    files: filesManifest,
  };

  // Pre-init IndexedDB stub. Without this, a user who starts a second upload while the first
  // is in flight and reloads the page before backend init returns (~100-500ms RTT) loses
  // the upload card entirely the backend has the PENDING_CHUNKS row, IDB has nothing, and
  // ResumePreviousUploadBanner has nothing to surface. The orphan then sits silently until
  // the 3-day failAbandonedPackages sweep. Writing a `preInit: true` stub immediately closes
  // that window to microseconds. The full init request body is embedded so rehydration can
  // re-call /packages (idempotent for resumable statuses) to fetch sourceFileIds and surface
  // a normal re-pick paused card.
  try {
    await savePendingPackage({
      uploadId: uploadMetadata.uploadId,
      packageName: uploadMetadata.packageName,
      totalSize,
      createdAt: Date.now(),
      preInit: true,
      preInitRequest: initRequestBody,
      files: formData.files.map((file) => ({
        sourceFileId: null,
        originalName: file.name,
        sizeBytes: file.size,
        fingerprint: fileFingerprint(file),
        fsHandle: file.__fsHandle || null,
        tusEndpoint: null,
        tusUploadUrl: null,
      })),
    });
  } catch (err) {
    console.warn("[ChunkedUploadService] pre-init persistence save failed", err?.message);
  }

  const initRequestId = uuidv4();
  let packageInfo;
  try {
    const initResponse = await http.post("/api/v1/upload/packages", initRequestBody, {
      headers: {
        Authorization: `Bearer ${tokenCache.token}`,
        "x-request-id": initRequestId,
        "X-Upload-Id": uploadMetadata.uploadId,
        [UPLOAD_SESSION_HEADER]: getUploadSessionId(),
      },
    });
    packageInfo = initResponse.data;
  } catch (error) {
    // Backend init failed drop the pre-init stub so a reload doesn't try to re-init against
    // a row that was never created. (A network-only failure where the backend actually did
    // create the row would naturally be rediscovered on the next attempt with the same
    // uploadId via the existing-record path; if the user just gives up, the new
    // detectStuckPendingChunks alert surfaces it.)
    removePendingPackage(uploadMetadata.uploadId).catch(() => {});
    // CRITICAL: release the queue slot that acquireUploadSlot claimed synchronously at
    // function entry. Without this, a POST /packages that fails (401 auth, 409 cross-session
    // conflict, network) leaves activePackageId pointing at this dead upload FOREVER.
    // Consequences observed after a cross-session 409 → take-over flow: the user's next
    // resume goes into "Waiting for the previous upload to finish…" because
    // acquireUploadSlot sees activePackageId != null and queues instead of activating.
    releaseUploadSlot(resolvedUploadId);
    if (error?.response?.status === 401) {
      throw buildAuthError(401, error);
    }
    // Single-active-upload guard: server rejects if the contributor already has a
    // non-terminal upload. Surface as a structured error so the caller can render the
    // takeover/resume prompt with the existing uploadId + status.
    if (error?.response?.status === 409 && error?.response?.data?.conflict) {
      const details = error.response.data.conflict;
      const conflictError = new Error(
        "You already have an upload in progress; take over or resume it before starting a new one."
      );
      conflictError.code = "UPLOAD_ACTIVE_ELSEWHERE";
      conflictError.userMessage = conflictError.message;
      conflictError.conflict = {
        existingUploadId: details.existingUploadId,
        existingStatus: details.existingStatus,
        ownerSessionId: details.ownerSessionId,
        existingPackageName: details.existingPackageName,
        currentSessionId: getUploadSessionId(),
      };
      throw conflictError;
    }
    captureUploadRequestErrorTelemetry({
      requestId: initRequestId,
      error,
      fallbackUrl: `${http.defaults.baseURL || ""}/api/v1/upload/packages`,
    });
    throw error;
  }

  if (!packageInfo?.expectedFiles?.length) {
    throw new Error("Package init returned no expected files");
  }

  // Persist enough to drive resume-on-reload. Filenames + sizes let the banner ask the user to
  // re-drop the same files; sourceFileId + tus URL let us pick up at the last acknowledged byte.
  try {
    await savePendingPackage({
      uploadId: packageInfo.uploadId,
      packageName: packageInfo.packageName || uploadMetadata.packageName,
      totalSize,
      // FIFO ordering across sessions: persist createdAt so a rehydrated paused package
      // restores its original position relative to siblings instead of being sorted by
      // whatever order IndexedDB happens to return them in on reload.
      createdAt: Date.now(),
      files: packageInfo.expectedFiles.map((expected) => {
        const candidate = formData.files.find((f) => f.name === expected.originalName);
        return {
          sourceFileId: expected.sourceFileId,
          originalName: expected.originalName,
          sizeBytes: expected.sizeBytesExpected,
          fingerprint: candidate ? fileFingerprint(candidate) : null,
          // FileSystemFileHandle is structured-cloneable into IndexedDB on Chromium.
          // Tagged onto the File by the DropZone when the user picked via showOpenFilePicker;
          // null for drag-drop or unsupported browsers, which falls back to the reselect card.
          fsHandle: candidate?.__fsHandle || null,
          tusEndpoint: expected.tusEndpoint,
          tusUploadUrl: expected.tusUploadUrl || null,
        };
      }),
    });
  } catch (err) {
    console.warn("[ChunkedUploadService] persistence save failed", err?.message);
  }

  uploadTelemetryService.start(packageInfo.uploadId, { totalBytes: totalSize });

  const baseUrl = http.defaults.baseURL || "";
  const headersRef = {
    Authorization: `Bearer ${tokenCache.token}`,
    [UPLOAD_SESSION_HEADER]: getUploadSessionId(),
  };
  const fileProgress = new Map();
  const tusByFile = new Map();
  // Parallel to tusByFile: sourceFileId -> watchdog handle. Populated when startSingle spins
  // up a tus.Upload; consumed by onSuccess/onError to stop the poll timer, and by the session
  // abort() to release all active watchdogs at once.
  const watchdogsByFile = new Map();
  let aggregateLoaded = 0;
  let completedCount = 0;
  let terminalError = null;
  // Track every fire-and-forget markFileUploaded write so we can flush them before
  // removePendingPackage runs. Without this barrier, the delete can race a still-pending
  // write the write resurrects the deleted record, and on next reload the user sees a
  // "paused" copy of a successfully-completed package.
  const markUploadedPromises = [];

  const tokenRefreshTimer = setInterval(async () => {
    try {
      const fresh = await refreshTokenIfNeeded();
      headersRef.Authorization = `Bearer ${fresh}`;
    } catch (err) {
      console.warn("[ChunkedUploadService] periodic token refresh failed", {
        message: err?.message,
      });
    }
  }, TOKEN_REFRESH_INTERVAL_MS);

  const progressSnapshotTimer = setInterval(() => {
    if (fileProgress.size === 0) return;
    const snapshot = {};
    fileProgress.forEach((bytes, sourceFileId) => {
      snapshot[sourceFileId] = bytes;
    });
    persistFileProgress(packageInfo.uploadId, snapshot).catch(() => {});
  }, PROGRESS_SNAPSHOT_INTERVAL_MS);

  const stopTokenRefresh = () => clearInterval(tokenRefreshTimer);
  const stopProgressSnapshot = () => clearInterval(progressSnapshotTimer);
  const removeFromInFlight = () => {
    const current = inFlight.get(packageInfo.uploadId);
    if (current === sessionRecord) {
      inFlight.delete(packageInfo.uploadId);
    }
    // Release the cross-tab ownership lock so other tabs (or a future reload of this tab)
    // can take over. Safe to call multiple times release is idempotent.
    try { sessionRecord.releaseOwnershipLock?.(); } catch (_) { /* ignore */ }
    sessionRecord.releaseOwnershipLock = null;
  };

  const updateAggregate = () => {
    aggregateLoaded = 0;
    for (const v of fileProgress.values()) {
      aggregateLoaded += v;
    }
    uploadTelemetryService.recordProgress(packageInfo.uploadId, {
      loaded: aggregateLoaded,
    });
    // As soon as every byte is sent, switch telemetry to FINALIZING. This (a) clears the
    // 15s stall timer so the user never sees the misleading "appears stalled" warning
    // while the server is briefly wrapping up, and (b) surfaces the friendly "finishing
    // package preparation" message until the SSE-driven server stage takes over.
    if (totalSize > 0 && aggregateLoaded >= totalSize) {
      uploadTelemetryService.markFinalizing(packageInfo.uploadId);
    }
    if (typeof onProgress !== "function") return;
    const percent = totalSize > 0
      ? Math.min(100, Math.round((aggregateLoaded / totalSize) * 100))
      : 0;
    const state = uploadTelemetryService.getState(packageInfo.uploadId);
    const speed = state?.rollingBps && state.rollingBps > 0
      ? state.rollingBps
      : state?.averageBps || 0;
    const remainingBytes = Math.max(0, totalSize - aggregateLoaded);
    const remainingSec = speed > 0 ? Math.round(remainingBytes / speed) : 0;
    onProgress(percent, remainingSec);
  };

  // Match expected files to raw File objects. Package init preserves order, but we resolve
  // by originalName for safety in case of duplicates or reordering.
  const filesByName = new Map();
  formData.files.forEach((file) => {
    const list = filesByName.get(file.name) || [];
    list.push(file);
    filesByName.set(file.name, list);
  });
  const pairs = packageInfo.expectedFiles.map((expected) => {
    const candidates = filesByName.get(expected.originalName);
    if (!candidates || candidates.length === 0) {
      throw new Error(`No File object available for "${expected.originalName}"`);
    }
    const file = candidates.shift();
    return { expected, file };
  });

  // Seed every file as "queued" before the first worker starts. Without this, the per-file
  // progress map only gets populated when a worker picks a file off the queue with
  // parallelFilesForConnection=1 that means the UI shows only the active file and queued
  // files are invisible until their turn arrives, which looks like data loss to the user.
  // startSingle() will overwrite the status to "uploading" as workers consume the queue.
  pairs.forEach(({ expected, file }) => {
    updatePerFile(packageInfo.uploadId, expected.sourceFileId, {
      sourceFileId: expected.sourceFileId,
      originalName: file.name,
      sizeBytes: file.size,
      loaded: 0,
      status: "queued",
      retries: 0,
    });
  });

  const startSingle = ({ expected, file, resolveOne, rejectOne }) => {
    const endpoint = expected.tusEndpoint.startsWith("http")
      ? expected.tusEndpoint
      : `${baseUrl}${expected.tusEndpoint}`;

    // Keep the byte count a requeued (auto-paused) file already reported so the row doesn't
    // flash back to 0 before tus's HEAD re-syncs the offset.
    const previouslyLoaded =
      perFileProgress.get(packageInfo.uploadId)?.get(expected.sourceFileId)?.loaded || 0;
    updatePerFile(packageInfo.uploadId, expected.sourceFileId, {
      sourceFileId: expected.sourceFileId,
      originalName: file.name,
      sizeBytes: file.size,
      loaded: previouslyLoaded,
      status: "uploading",
      retries: 0,
    });

    const tusUpload = new tus.Upload(file, {
      endpoint,
      // Normalize to an absolute URL — tus-js-client fetches it as-is, and a relative URL
      // resolves against the page origin. When FE and backend share an origin that's fine,
      // but any split-origin setup (local dev at :3000/:8080, or a CDN in front of the FE)
      // sends HEAD to the wrong host — often a static server that returns 200 + HTML with
      // no Upload-Offset, causing tus-js-client to retry forever. Reproduced end-to-end as
      // the "B stuck at 93% on the tail file" symptom for cross-device takeovers.
      uploadUrl: toAbsoluteTusUrl(baseUrl, expected.tusUploadUrl),
      metadata: {
        packageId: packageInfo.uploadId,
        sourceFileId: expected.sourceFileId,
        filename: file.name,
        contentType: file.type || "application/octet-stream",
      },
      headers: headersRef,
      chunkSize: chunkSizeForConnection(),
      // CRITICAL: namespace tus-js-client's URL storage per (packageId, sourceFileId). The
      // default fingerprint is just name|size|type|lastModified, so when the user has two
      // packages (different windows / incognito / queued sequentially) containing the same
      // physical file, both packages share a localStorage entry under the same fingerprint.
      // findPreviousUploads in our resume path would then return a SIBLING package's TUS
      // URL start() would HEAD it, see offset==length (sibling already uploaded), fire
      // onSuccess against the wrong upload, and markFileUploaded for OUR sourceFileId even
      // though the server never received a single byte against our SourceFile. The package
      // then sits forever at one file short of complete (observed end-to-end on packages
      // 150145 and 150150). Scoping the fingerprint by uploadId + sourceFileId guarantees
      // findPreviousUploads can only see this package's own prior URLs.
      fingerprint: makeNamespacedFingerprint(packageInfo.uploadId, expected.sourceFileId),
      // Always 1: `parallelUploads > 1` would invoke the TUS Concatenation extension and
      // split the file into partial uploads whose POSTs strip our custom Upload-Metadata,
      // breaking ownership validation. Throughput comes from per-package file-level
      // parallelism (parallelFilesForConnection) instead.
      parallelUploads: 1,
      retryDelays: TUS_RETRY_DELAYS,
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      onShouldRetry: (err, retryAttempt) => {
        const status = err?.originalResponse?.getStatus?.();
        const responseText = err?.originalResponse?.getBody?.();
        const pkg = perFileProgress.get(packageInfo.uploadId);
        const prev = pkg?.get(expected.sourceFileId);
        updatePerFile(packageInfo.uploadId, expected.sourceFileId, {
          retries: (prev?.retries || 0) + 1,
          status: "retrying",
        });
        const decide = (v, reason) => {
          console.info('[UPLOAD-DIAG] upload/onShouldRetry', {
            sourceFileId: expected.sourceFileId,
            name: expected.originalName,
            attempt: retryAttempt,
            status,
            responseSnippet: typeof responseText === 'string' ? responseText.slice(0, 200) : null,
            errMsg: err?.message,
            decision: v ? 'RETRY' : 'GIVE_UP',
            reason,
          });
          return v;
        };
        if (status === 401) {
          refreshTokenIfNeeded({ force: true })
            .then((fresh) => { headersRef.Authorization = `Bearer ${fresh}`; })
            .catch(() => {});
          return decide(true, 'refresh-token');
        }
        if (status === 403 || status === 404 || status === 410) {
          return decide(false, 'unrecoverable-4xx');
        }
        // 409 for a TUS PATCH is spec-defined as an Upload-Offset conflict —
        // server drifted from client (usually because a prior PATCH's bytes
        // landed but its response was lost, so our next PATCH sent from the
        // stale offset). tus-js-client's built-in retry does a HEAD to re-
        // sync the offset before the next PATCH, so letting it retry recovers
        // cleanly. Previously we short-circuited 409 as unrecoverable, which
        // failed entire uploads on a single mid-flight race. See 2026-08-05
        // incident (IMG_5977.mov, 33849344 vs 34373632).
        if (status === 409) {
          return decide(retryAttempt < TUS_RETRY_DELAYS.length, 'tus-offset-conflict');
        }
        // 423 LOCKED = another device took over ownership. Retrying would just burn the tail
        // budget with more 423s; the redux catch will surface the "taken over" spectator state.
        if (status === 423) {
          return decide(false, 'session-locked');
        }
        return decide(retryAttempt < TUS_RETRY_DELAYS.length, `budget=${retryAttempt}/${TUS_RETRY_DELAYS.length}`);
      },
      onProgress: (bytesLoaded) => {
        fileProgress.set(expected.sourceFileId, bytesLoaded);
        updatePerFile(packageInfo.uploadId, expected.sourceFileId, {
          loaded: bytesLoaded,
          status: "uploading",
        });
        watchdog.notifyProgress();
        updateAggregate();
      },
      onSuccess: () => {
        fileProgress.set(expected.sourceFileId, file.size);
        updatePerFile(packageInfo.uploadId, expected.sourceFileId, {
          loaded: file.size,
          status: "uploaded",
        });
        watchdog.stop();
        watchdogsByFile.delete(expected.sourceFileId);
        // Persist immediately (don't wait for the 5s snapshot tick) so a reload between
        // now and the next snapshot doesn't rehydrate this file as "still uploading".
        markUploadedPromises.push(
          markFileUploaded(packageInfo.uploadId, expected.sourceFileId).catch(() => {})
        );
        // Drop from the in-flight set so the offline/online handlers (and cancel) only
        // touch uploads that are actually still running. Leaving completed uploads in
        // tusByFile causes a network blip to re-`.start()` them â€” with
        // removeFingerprintOnSuccess: true the fingerprint is already gone, so they
        // restart from byte 0 instead of being a no-op.
        tusByFile.delete(expected.sourceFileId);
        updateAggregate();
        resolveOne();
      },
      onError: (err) => {
        const status = err?.originalResponse?.getStatus?.();
        const responseBody = err?.originalResponse?.getBody?.();
        console.info('[UPLOAD-DIAG] upload/onError', {
          sourceFileId: expected.sourceFileId,
          name: expected.originalName,
          status,
          errMsg: err?.message,
          responseSnippet: typeof responseBody === 'string' ? responseBody.slice(0, 300) : null,
        });
        // 423 from the TUS PATCH guard means the row's session_id no longer matches ours —
        // another device claimed ownership. Wrap the error so the redux thunk can flip this
        // card into spectator mode instead of surfacing it as a generic failure. Also cancel
        // any sibling files still uploading in the same package — they'd all 423 too.
        if (status === 423) {
          const takenOverError = new Error(
            "This upload was taken over by another device."
          );
          takenOverError.code = "UPLOAD_TAKEN_OVER";
          takenOverError.userMessage =
            "Uploading — taken over by another device.";
          takenOverError.uploadId = packageInfo.uploadId;
          updatePerFile(packageInfo.uploadId, expected.sourceFileId, {
            status: "paused",
            error: null,
          });
          watchdog.stop();
          watchdogsByFile.delete(expected.sourceFileId);
          tusByFile.delete(expected.sourceFileId);
          // Abort sibling in-flight uploads so we don't keep firing 423s. The winning device
          // still holds the row.
          for (const [siblingId, siblingTus] of tusByFile.entries()) {
            if (siblingId === expected.sourceFileId) continue;
            try { siblingTus.abort(false); } catch {}
          }
          rejectOne(takenOverError);
          return;
        }
        // Auto-pause on transient errors (bug fix 2026-08). Prior to this,
        // every non-423 tus.onError → status: "failed" → outer catch dispatched
        // Redux FAILED → card hid, backend row stayed alive → invisible
        // zombie the user couldn't resume. classifyTusError distinguishes
        // recoverable network/5xx/408/401 errors from real client failures.
        const classification = classifyTusError(err);
        if (classification.transient) {
          console.info('[UPLOAD-DIAG] upload/auto-pause-on-transient-error', {
            uploadId: packageInfo.uploadId,
            sourceFileId: expected.sourceFileId,
            reason: classification.reason,
            status,
            errMsg: err?.message,
          });
          // Retire this tus.Upload: its retry budget is spent and its socket state is
          // suspect (long sleep / dead TCP). Drop it from tusByFile so resumeInFlight and
          // the online/nudge handlers don't restart the stale instance; the worker pool
          // requeues the pair and builds a fresh tus.Upload on resume, carrying the TUS
          // URL forward so the HEAD/PATCH continue from the server's offset.
          const resumeUrl = tusUpload.url || expected.tusUploadUrl || null;
          watchdog.stop();
          watchdogsByFile.delete(expected.sourceFileId);
          tusByFile.delete(expected.sourceFileId);
          try { tusUpload.abort(false); } catch {}
          // pauseInFlight aborts sibling tus.Uploads too and does the full
          // pause plumbing (session.paused, resumeGate, uploadTelemetryService
          // .pause, IndexedDB persistPaused, backend notifyPause). If network
          // was too bad for one file, the others can't reasonably keep going.
          try { pauseInFlight(packageInfo.uploadId, "auto"); } catch (pauseErr) {
            console.warn('[UPLOAD-DIAG] auto-pause-failed', { uploadId: packageInfo.uploadId, message: pauseErr?.message });
          }
          updatePerFile(packageInfo.uploadId, expected.sourceFileId, {
            status: "paused",
            error: null,
          });
          // Side-channel to Redux (store.js subscribeAutoPause): the worker pool stays
          // alive, so the upload() promise no longer rejects and the thunk catch can't
          // flip the card to PAUSED for us.
          notifyAutoPause({
            uploadId: packageInfo.uploadId,
            reason: classification.reason,
            message: err?.message || String(err),
          });
          resolveOne(buildAutoPauseSentinel({
            expected: { ...expected, tusUploadUrl: resumeUrl },
            file,
          }));
          return;
        }
        updatePerFile(packageInfo.uploadId, expected.sourceFileId, {
          status: "failed",
          error: err?.message || String(err),
        });
        watchdog.stop();
        watchdogsByFile.delete(expected.sourceFileId);
        tusByFile.delete(expected.sourceFileId);
        rejectOne(err);
      },
    });

    tusByFile.set(expected.sourceFileId, tusUpload);
    const watchdog = createStallWatchdog({
      uploadId: packageInfo.uploadId,
      sourceFileId: expected.sourceFileId,
      sessionRecord,
      filename: file.name,
    });
    watchdogsByFile.set(expected.sourceFileId, watchdog);
    watchdog.attach(tusUpload);
    tusUpload.start();
  };

  const sessionRecord = {
    tusByFile,
    eventsStream: null,
    paused: false,
    cancelled: false,
    // Promise the worker loop awaits while paused; resolved by resumeInFlight to release workers.
    resumeGate: null,
    resolveResume: null,
    abort: () => {
      sessionRecord.cancelled = true;
      for (const w of watchdogsByFile.values()) {
        try { w.stop(); } catch {}
      }
      watchdogsByFile.clear();
      for (const t of tusByFile.values()) {
        try { t.abort(true); } catch {}
      }
      if (sessionRecord.eventsStream) {
        try { sessionRecord.eventsStream.close(); } catch {}
        sessionRecord.eventsStream = null;
      }
      // Release any worker waiting on the resume gate so they unblock and exit cleanly.
      if (sessionRecord.resolveResume) {
        sessionRecord.resolveResume();
        sessionRecord.resolveResume = null;
        sessionRecord.resumeGate = null;
      }
      // Free the queue slot â€” whether we were the active package or sitting in the waiting list.
      releaseUploadSlot(packageInfo.uploadId);
    },
  };
  inFlight.set(packageInfo.uploadId, sessionRecord);

  // Cross-tab ownership lock: held for the lifetime of this upload session in this tab.
  // Other tabs see this lock via useUploadOwnership() and render "Uploading in another
  // tab" instead of the paused-card UI. This must be granted BEFORE we start the upload
  // workers; otherwise a secondary tab can spin a competing client loop while merely queued
  // behind the true owner in the Web Locks scheduler.
  const releaseOwnershipLock = await acquireUploadLock(packageInfo.uploadId);
  if (!releaseOwnershipLock) {
    stopTokenRefresh();
    stopProgressSnapshot();
    removeFromInFlight();
    releaseUploadSlot(packageInfo.uploadId);
    throw buildOwnedByOtherTabError(packageInfo.uploadId);
  }
  if (inFlight.get(packageInfo.uploadId) === sessionRecord) {
    sessionRecord.releaseOwnershipLock = releaseOwnershipLock;
  } else {
    try { releaseOwnershipLock(); } catch (_) { /* ignore */ }
  }

  // Slot was reserved synchronously at function entry (slotPromise). If we're queued the
  // promise resolves later when our turn comes; if we're already active it resolved immediately.
  if (activePackageId !== packageInfo.uploadId) {
    uploadTelemetryService.markWaiting(packageInfo.uploadId);
  }
  await slotPromise;
  if (sessionRecord.cancelled) {
    // User cancelled while we were queued. The thunk already dispatched CANCELED, so we just
    // need to throw something the catch path can clean up after.
    const aborted = new Error("Upload cancelled while queued");
    aborted.name = "AbortError";
    aborted.code = "CANCELLED_QUEUED";
    stopTokenRefresh();
    stopProgressSnapshot();
    removeFromInFlight();
    throw aborted;
  }

  const maxConcurrentFiles = parallelFilesForConnection();

  const runOne = (pair) =>
    new Promise((resolveOne, rejectOne) =>
      startSingle({ ...pair, resolveOne, rejectOne })
    );

  try {
    const pool = await runFileWorkerPool({
      pairs,
      maxConcurrentFiles,
      runOne,
      sessionRecord,
      tusByFile,
    });
    terminalError = pool.terminalError;
    completedCount = pool.completedCount;

    if (sessionRecord.cancelled && !terminalError) {
      // Cancelled while paused: the pool unparked from the gate and exited. Same shape as
      // the cancelled-while-queued path so the thunk's catch treats it as an abort.
      const aborted = new Error("Upload cancelled while paused");
      aborted.name = "AbortError";
      aborted.code = "CANCELLED_QUEUED";
      throw aborted;
    }

    if (terminalError) {
      const { passthrough, error } = decideTerminalErrorDisposition(terminalError, pool.failingFileName);
      if (passthrough) {
        throw error;
      }
      uploadTelemetryService.fail(packageInfo.uploadId);
      captureUploadRequestErrorTelemetry({
        requestId: initRequestId,
        error: terminalError,
        fallbackUrl: `${baseUrl}/api/v1/upload/tus/`,
      });
      throw error;
    }

    if (completedCount !== pairs.length) {
      uploadTelemetryService.fail(packageInfo.uploadId);
      throw new Error(`Expected ${pairs.length} file uploads, completed ${completedCount}`);
    }

    // Release the queue slot now â€” bytes are in, so the next queued package can start while
    // we sit on the SSE stream for assembly / CatDV / publish (those can take many minutes).
    releaseUploadSlot(packageInfo.uploadId);

    uploadTelemetryService.markFinalizing(packageInfo.uploadId);
    // Flush any in-flight markFileUploaded writes so a backend-status-driven cleanup down the
    // line (inside waitForBackendUploadReady) doesn't race a write that resurrects the
    // IndexedDB entry. The actual removePendingPackage call is deferred â€” see below.
    await Promise.all(markUploadedPromises);
    // NOTE: removePendingPackage is INTENTIONALLY not called here. The persisted record is
    // kept until the backend reaches a truly-terminal status (PUBLISHED/UPLOADED/FAILED/etc.)
    // so a reload during backend processing or a backend stall still surfaces the upload via
    // boot rehydration. waitForBackendUploadReady handles the deletion once the backend
    // verdict is in.

    await waitForBackendUploadReady({
      uploadId: packageInfo.uploadId,
      sessionRecord,
      onErrorLogPrefix: "[ChunkedUploadService] SSE error",
      onTerminal: removeFromInFlight,
    });

    uploadTelemetryService.complete(packageInfo.uploadId);
    stopTokenRefresh();
    stopProgressSnapshot();
    return {
      uploadId: packageInfo.uploadId,
      directoryName: packageInfo.directoryName,
      packageName: packageInfo.packageName,
      status: packageInfo.status,
    };
  } catch (err) {
    stopTokenRefresh();
    stopProgressSnapshot();
    releaseUploadSlot(packageInfo.uploadId);
    removeFromInFlight();
    throw err;
  }
};

const cancel = async (uploadId, reason, notes) => {
  const session = inFlight.get(uploadId);
  if (session) {
    session.abort();
    inFlight.delete(uploadId);
    // Release the cross-tab ownership lock â€” abort() doesn't, because pause also calls
    // abort-equivalent paths and we want pause to keep ownership in the paused tab.
    try { session.releaseOwnershipLock?.(); } catch (_) { /* ignore */ }
    session.releaseOwnershipLock = null;
  }
  // Drop any reservation a rehydrated paused package was holding â€” without this, cancelling
  // the rehydrated package would leave its reservation behind and stall the queue for any
  // upload that started while it was still rehydrated.
  releaseReservedSlot(uploadId);
  // Observability: mark terminal BEFORE uploadTelemetryService.fail() so the
  // diagnostics bridge sees UPLOAD_CANCELLED and then dispose stops the
  // subscription — that way the subsequent .fail() doesn't emit a stray
  // UPLOAD_FAILED into the timeline.
  try {
    announceUploadTerminal(uploadId, "cancelled", { reason, notes });
  } catch (_error) {
    /* observability must never break cancel */
  }
  uploadTelemetryService.fail(uploadId);
  removePendingPackage(uploadId).catch(() => {});
  clearPerFile(uploadId);
  // Persist the cancel intent BEFORE the network attempt so a reload that
  // races the POST doesn't lose the signal. The synth path in packagesSlice
  // reads this queue and refuses to resurrect the row as a phantom paused
  // card; the boot-time drainer (see below) retries the POST until the
  // backend acknowledges.
  enqueuePendingCancel(uploadId, reason, notes);
  try {
    await notifyServerCancelWithRetry(uploadId, reason, notes);
    removePendingCancel(uploadId);
  } catch (err) {
    console.warn("[ChunkedUploadService] cancel notify failed after retries; queued for boot drain", {
      uploadId,
      message: err?.message,
      status: err?.response?.status,
    });
  }
  return { uploadId, cancelled: true };
};

// Returns the promise (not fire-and-forget) so cancel() can await confirmation
// and the boot-time drainer can retry entries whose backend POST never landed.
// A 404 is treated as success — the row is already gone from the backend, so
// there's nothing left to cancel.
const notifyServerCancel = async (uploadId, reason, notes) => {
  const token = await refreshTokenIfNeeded();
  try {
    await http.post(
      `/api/v1/upload/packages/${uploadId}/cancel`,
      { reason, notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (err) {
    if (err?.response?.status === 404) return; // row already gone
    throw err;
  }
};

// Immediate-retry wrapper. The boot-time drainer only fires on module load,
// so a transient failure at click time (backend hiccup, race with concurrent
// PATCH, brief 5xx) would otherwise leave the row alive until the user
// reloaded the page. Three attempts (500ms, 1s, 2s backoff) cover the
// common transient window without meaningfully delaying cancel UX. 4xx
// errors are permanent (auth, validation) and skip retry to avoid burning
// budget on requests that will never succeed.
const NOTIFY_CANCEL_RETRY_ATTEMPTS = 3;
const NOTIFY_CANCEL_RETRY_BACKOFF_MS = [500, 1000, 2000];

const notifyServerCancelWithRetry = async (uploadId, reason, notes) => {
  let lastError = null;
  for (let attempt = 0; attempt < NOTIFY_CANCEL_RETRY_ATTEMPTS; attempt += 1) {
    try {
      await notifyServerCancel(uploadId, reason, notes);
      return;
    } catch (err) {
      lastError = err;
      const status = err?.response?.status;
      // 4xx are permanent — don't retry. 5xx and no-response (network) are
      // worth retrying because they're typically transient.
      if (typeof status === "number" && status >= 400 && status < 500) {
        throw err;
      }
      if (attempt < NOTIFY_CANCEL_RETRY_ATTEMPTS - 1) {
        const backoff = NOTIFY_CANCEL_RETRY_BACKOFF_MS[attempt] ?? 2000;
        await new Promise((resolve) => setTimeout(resolve, backoff));
      }
    }
  }
  throw lastError;
};

export const __notifyServerCancelWithRetryForTest = notifyServerCancelWithRetry;

// Drain pending cancels from prior sessions or from click-time attempts
// whose immediate retries were exhausted. Fired on:
//   - module load (once per boot)
//   - visibility wake (user returns to tab)
//   - periodic tick every 60s while the queue is non-empty (belt-and-braces
//     for the tab-open-for-hours case)
// Entries older than 24h are pruned first so a permanently unreachable row
// can't wedge the queue.
const drainPendingCancels = async () => {
  try {
    prunePendingCancels();
    for (const entry of listPendingCancels()) {
      try {
        await notifyServerCancel(entry.uploadId, entry.reason, entry.notes);
        removePendingCancel(entry.uploadId);
      } catch (err) {
        // Backend still unreachable — leave in queue for next drain tick.
        console.warn("[ChunkedUploadService] pending cancel retry failed", {
          uploadId: entry.uploadId,
          message: err?.message,
          status: err?.response?.status,
        });
      }
    }
  } catch (_error) {
    /* drainer must never throw */
  }
};

const PENDING_CANCEL_DRAIN_INTERVAL_MS = 60_000;

if (typeof setTimeout === "function") {
  // Defer so import order doesn't matter — auth service must be ready before
  // refreshTokenIfNeeded resolves.
  setTimeout(() => { drainPendingCancels(); }, 0);
}
if (typeof setInterval === "function") {
  // No-op when queue is empty (loop over 0 entries), so this is cheap.
  setInterval(() => {
    if (listPendingCancels().length > 0) drainPendingCancels();
  }, PENDING_CANCEL_DRAIN_INTERVAL_MS);
}
if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && listPendingCancels().length > 0) {
      drainPendingCancels();
    }
  });
}

const notifyServerPause = (uploadId) => {
  refreshTokenIfNeeded()
    .then((token) => http.post(
      `/api/v1/upload/packages/${uploadId}/pause`,
      null,
      { headers: { Authorization: `Bearer ${token}` } }
    ))
    .catch((err) => {
      console.warn("[ChunkedUploadService] pause notify failed", {
        uploadId,
        message: err?.message,
        status: err?.response?.status,
      });
    });
};

// Exported for the rehydrate banner: a page reload is effectively a pause, but the backend
// never receives the pauseInFlight call because the session died with the tab. The banner
// invokes this for every rehydrated package so the DB reflects reality. Race-wise this is
// safe: boot runs before the user can interact with the resume card, and even if /pause
// somehow lands after the user's first TUS PATCH, the next PATCH flips PAUSED back to
// CHUNK_UPLOAD_IN_PROGRESS via TusLifecycleService â€” so the inconsistency is short-lived.
export const notifyPauseFromRehydrate = (uploadId) => notifyServerPause(uploadId);

/**
 * Wake every in-flight tus.Upload from its retry backoff (bug 2026-08-05).
 *
 * When tus enters onError, it schedules the next attempt via
 * setTimeout(fn, TUS_RETRY_DELAYS[attempt]) — up to 240s per slot. If the
 * network improves mid-sleep, tus doesn't notice until the sleep expires:
 * that's the 1–2 min lag users report after switching from poor mobile
 * data to fast WiFi.
 *
 * NetworkRecoveryWatcher fires this on online / connection.change events
 * (filtered by shouldTriggerRecoveryNudge). abort(false) cancels the
 * pending retry timer; start() fires a fresh PATCH immediately. If the
 * network is still degraded, tus falls back into the retry loop naturally.
 *
 * Skips paused/cancelled sessions — they belong to pauseInFlight and the
 * user's Resume click, not to auto-recovery.
 *
 * Global cool-down (`recoveryNudgeCoolDownMs`) prevents thrashing on
 * rapid connection.change events (WiFi flapping, network-type switches
 * that fire several events in quick succession).
 */
let lastRecoveryNudgeAt = 0;
const NUDGE_COOLDOWN_MS = 3_000;
export const nudgeInFlightUploads = () => {
  const now = Date.now();
  if (now - lastRecoveryNudgeAt < NUDGE_COOLDOWN_MS) return 0;
  lastRecoveryNudgeAt = now;

  let nudged = 0;
  for (const [uploadId, session] of inFlight.entries()) {
    if (!session || session.paused || session.cancelled) continue;
    for (const t of session.tusByFile.values()) {
      try {
        t.abort(false);
      } catch (err) {
        console.warn("[UPLOAD-DIAG] nudgeInFlight/abort-error", {
          uploadId,
          message: err?.message,
        });
      }
      try {
        t.start();
        nudged += 1;
      } catch (err) {
        console.warn("[UPLOAD-DIAG] nudgeInFlight/start-error", {
          uploadId,
          message: err?.message,
        });
      }
    }
  }
  if (nudged > 0) {
    console.info("[UPLOAD-DIAG] nudgeInFlight/dispatched", { nudged });
  }
  return nudged;
};

// Full teardown for sign-out (2026-08-05 multi-tenancy leak fix). Aborts every
// active session, releases queue reservations, resolves waiters so awaiting
// promises don't hang, and drops per-file progress. Called from the store
// middleware on auth/signOut/fulfilled — without it, a manager who logs in
// after admin would still see admin's live tus.Upload activity draining into
// Redux via the progress callbacks captured in the closures.
export const abortAll = () => {
  for (const [, session] of inFlight.entries()) {
    try { session.abort(); } catch (_) { /* ignore */ }
    try { session.releaseOwnershipLock?.(); } catch (_) { /* ignore */ }
  }
  inFlight.clear();

  for (const waiter of queueWaiters.splice(0)) {
    try { waiter.resolve(); } catch (_) { /* ignore */ }
  }
  activePackageId = null;
  reservedSlotIds.clear();
  perFileProgress.clear();
  emitQueueSnapshot();
};

export const __inFlightForTest = inFlight;
export const __waitForBackendUploadReadyForTest = waitForBackendUploadReady;

/**
 * Resume an in-flight package using files re-dropped by the user after a page reload.
 * Expects `storedRecord` from uploadPersistence (uploadId + files manifest with tusUploadUrl).
 * Matches re-dropped files by fingerprint; throws if anything is missing.
 */
const resume = async (storedRecord, droppedFiles, onProgress) => {
  if (!storedRecord?.uploadId || !Array.isArray(storedRecord.files)) {
    throw new Error("Invalid stored package record");
  }
  if (!droppedFiles || droppedFiles.length === 0) {
    throw new Error("No files provided for resume");
  }

  // Reserve queue position SYNCHRONOUSLY before any await â€” see upload() for the rationale.
  // Without this, autoResume's chained dispatches race on token refresh and the wrong package
  // ends up active.
  const slotPromise = acquireUploadSlot(storedRecord.uploadId);

  try {
    await refreshTokenIfNeeded({ force: true });
  } catch (err) {
    releaseUploadSlot(storedRecord.uploadId);
    throw err;
  }

  // User chose to resume â€” clear the persisted paused flag so a mid-resume reload doesn't
  // present the upload as still paused.
  markPackagePaused(storedRecord.uploadId, false).catch(() => {});

  const byFingerprint = new Map();
  droppedFiles.forEach((file) => {
    const key = fileFingerprint(file);
    const matches = byFingerprint.get(key) || [];
    matches.push(file);
    byFingerprint.set(key, matches);
  });
  const fingerprintKeyForEntry = (entry) =>
    entry.fingerprint
      ? entry.fingerprint.split('|').slice(0, 2).join('|')
      : `${entry.originalName}|${entry.sizeBytes}`;
  const takeFileForEntry = (entry) => {
    const candidates = byFingerprint.get(fingerprintKeyForEntry(entry)) || [];
    return candidates.shift() || null;
  };

  // Split storedRecord.files into already-done (skip the TUS round-trip entirely; their
  // server-side session was destroyed at finalization, so re-uploading would actually re-
  // send bytes) and still-pending. Pre-seed the done set into fileProgress so the aggregate
  // progress accounts for already-uploaded bytes â€” otherwise the % would reset to whatever
  // the pending files contribute.
  const completedEntries = storedRecord.files.filter(isFileAlreadyUploaded);
  const pendingEntries = storedRecord.files.filter((entry) => !isFileAlreadyUploaded(entry));

  const pairs = pendingEntries.map((entry) => {
    const file = takeFileForEntry(entry);
    if (!file) {
      throw new Error(`Missing file for "${entry.originalName}". Please select the same file used for this upload.`);
    }
    return {
      expected: {
        sourceFileId: entry.sourceFileId,
        originalName: entry.originalName,
        sizeBytesExpected: entry.sizeBytes,
        tusEndpoint: entry.tusEndpoint || "/api/v1/upload/tus/",
        tusUploadUrl: entry.tusUploadUrl || null,
        bytesUploadedServerSide: entry.bytesUploaded || 0,
      },
      file,
    };
  });

  console.info('[UPLOAD-DIAG] resume:start', {
    uploadId: storedRecord.uploadId,
    totalStored: storedRecord.files.length,
    completed: completedEntries.length,
    pending: pendingEntries.length,
    pendingDetails: pairs.map((p) => ({
      sourceFileId: p.expected.sourceFileId,
      name: p.expected.originalName,
      sizeBytes: p.expected.sizeBytesExpected,
      serverBytesUploaded: p.expected.bytesUploadedServerSide,
      hasTusUrl: !!p.expected.tusUploadUrl,
      tusUrlTail: p.expected.tusUploadUrl ? p.expected.tusUploadUrl.split('/').pop() : null,
    })),
  });

  const baseUrl = http.defaults.baseURL || "";
  const headersRef = {
    Authorization: `Bearer ${tokenCache.token}`,
    [UPLOAD_SESSION_HEADER]: getUploadSessionId(),
  };
  const fileProgress = new Map();
  const tusByFile = new Map();
  // See upload() — parallel to tusByFile for the stall watchdog handles.
  const watchdogsByFile = new Map();
  let aggregateLoaded = 0;
  let completedCount = 0;
  let terminalError = null;
  // See upload() â€” track markFileUploaded writes so removePendingPackage doesn't race the
  // last file's persistence.
  const markUploadedPromises = [];

  // Account for already-completed files in both the aggregate byte count and the per-file
  // rows. Without this the progress bar would jump backwards (e.g., from 60% to 30% if half
  // the bytes belonged to already-uploaded files) and the UI would show completed files as
  // "Queued" until a worker reached them.
  completedEntries.forEach((entry) => {
    const sizeBytes = entry.sizeBytes || 0;
    fileProgress.set(entry.sourceFileId, sizeBytes);
    updatePerFile(storedRecord.uploadId, entry.sourceFileId, {
      sourceFileId: entry.sourceFileId,
      originalName: entry.originalName,
      sizeBytes,
      loaded: sizeBytes,
      status: "uploaded",
      retries: 0,
    });
  });

  // Seed every still-pending file as "queued" up front — mirrors the upload() startSingle
  // pre-seed at line 897. Without this the UI's "N/M files" counter grows as workers pick
  // files off the queue (2/3 → 3/4 → …) because filesForSummary.length only counts rows
  // present in perFileProgress. After takeover with a 6-file package that has 2 completed +
  // 4 pending, we want the card to show "N/6" from the first frame, not "N/3" climbing.
  pairs.forEach(({ expected, file }) => {
    updatePerFile(storedRecord.uploadId, expected.sourceFileId, {
      sourceFileId: expected.sourceFileId,
      originalName: file.name,
      sizeBytes: file.size,
      loaded: expected.bytesUploadedServerSide || 0,
      status: "queued",
      retries: 0,
    });
  });

  const totalSize = storedRecord.totalSize
    || (pairs.reduce((acc, p) => acc + (p.expected.sizeBytesExpected || 0), 0)
        + completedEntries.reduce((acc, e) => acc + (e.sizeBytes || 0), 0));

  // Observability: announce the resumed upload. Idempotent — a resume triggered
  // from the same browser session that started the upload will just update the
  // existing diagnostics context; a resume after page reload will hydrate a
  // fresh diagnostics object from scratch.
  try {
    announceUpload(storedRecord.uploadId, {
      uploader: "chunked",
      fileCount: storedRecord.files.length,
      totalSize,
      packageName: storedRecord.packageName,
      uploadSessionId: getUploadSessionId(),
      chunkedUploadService: { subscribePerFileProgress },
    });
  } catch (_error) {
    /* observability must never break resume */
  }

  uploadTelemetryService.start(storedRecord.uploadId, { totalBytes: totalSize });

  const tokenRefreshTimer = setInterval(async () => {
    try {
      const fresh = await refreshTokenIfNeeded();
      headersRef.Authorization = `Bearer ${fresh}`;
    } catch (err) {
      console.warn("[ChunkedUploadService] resume periodic token refresh failed", err?.message);
    }
  }, TOKEN_REFRESH_INTERVAL_MS);
  const stopTokenRefresh = () => clearInterval(tokenRefreshTimer);

  const progressSnapshotTimer = setInterval(() => {
    if (fileProgress.size === 0) return;
    const snapshot = {};
    fileProgress.forEach((bytes, sourceFileId) => {
      snapshot[sourceFileId] = bytes;
    });
    persistFileProgress(storedRecord.uploadId, snapshot).catch(() => {});
  }, PROGRESS_SNAPSHOT_INTERVAL_MS);
  const stopProgressSnapshot = () => clearInterval(progressSnapshotTimer);

  const sessionRecord = {
    tusByFile,
    eventsStream: null,
    paused: false,
    cancelled: false,
    resumeGate: null,
    resolveResume: null,
    abort: () => {
      sessionRecord.cancelled = true;
      for (const w of watchdogsByFile.values()) {
        try { w.stop(); } catch {}
      }
      watchdogsByFile.clear();
      for (const t of tusByFile.values()) {
        try { t.abort(true); } catch {}
      }
      if (sessionRecord.eventsStream) {
        try { sessionRecord.eventsStream.close(); } catch {}
        sessionRecord.eventsStream = null;
      }
      if (sessionRecord.resolveResume) {
        sessionRecord.resolveResume();
        sessionRecord.resolveResume = null;
        sessionRecord.resumeGate = null;
      }
      releaseUploadSlot(storedRecord.uploadId);
    },
  };
  inFlight.set(storedRecord.uploadId, sessionRecord);

  // Cross-tab ownership lock (same as the fresh-upload path above — see comment there).
  const resumeOwnershipLock = await acquireUploadLock(storedRecord.uploadId);
  if (!resumeOwnershipLock) {
    stopTokenRefresh();
    stopProgressSnapshot();
    releaseUploadSlot(storedRecord.uploadId);
    inFlight.delete(storedRecord.uploadId);
    throw buildOwnedByOtherTabError(storedRecord.uploadId);
  }
  if (inFlight.get(storedRecord.uploadId) === sessionRecord) {
    sessionRecord.releaseOwnershipLock = resumeOwnershipLock;
  } else {
    try { resumeOwnershipLock(); } catch (_) { /* ignore */ }
  }

  const removeFromInFlight = () => {
    const current = inFlight.get(storedRecord.uploadId);
    if (current === sessionRecord) {
      inFlight.delete(storedRecord.uploadId);
    }
    try { sessionRecord.releaseOwnershipLock?.(); } catch (_) { /* ignore */ }
    sessionRecord.releaseOwnershipLock = null;
  };

  const updateAggregate = () => {
    aggregateLoaded = 0;
    for (const v of fileProgress.values()) aggregateLoaded += v;
    uploadTelemetryService.recordProgress(storedRecord.uploadId, { loaded: aggregateLoaded });
    // Same FINALIZING-on-100% behaviour as the fresh upload path: clears the stall timer and
    // surfaces the "finishing package preparation" stage label immediately.
    if (totalSize > 0 && aggregateLoaded >= totalSize) {
      uploadTelemetryService.markFinalizing(storedRecord.uploadId);
    }
    if (typeof onProgress !== "function") return;
    const percent = totalSize > 0
      ? Math.min(100, Math.round((aggregateLoaded / totalSize) * 100))
      : 0;
    const state = uploadTelemetryService.getState(storedRecord.uploadId);
    const speed = state?.rollingBps && state.rollingBps > 0 ? state.rollingBps : state?.averageBps || 0;
    const remainingBytes = Math.max(0, totalSize - aggregateLoaded);
    const remainingSec = speed > 0 ? Math.round(remainingBytes / speed) : 0;
    onProgress(percent, remainingSec);
  };

  const runOne = (pair) => new Promise((resolveOne, rejectOne) => {
    const endpoint = pair.expected.tusEndpoint.startsWith("http")
      ? pair.expected.tusEndpoint
      : `${baseUrl}${pair.expected.tusEndpoint}`;
    // Seed the per-file row to "uploading" â€” for rehydrated paused uploads the row was
    // previously in the "paused" state and would otherwise stay that way until the first
    // onProgress fires.
    updatePerFile(storedRecord.uploadId, pair.expected.sourceFileId, {
      sourceFileId: pair.expected.sourceFileId,
      originalName: pair.file.name,
      sizeBytes: pair.file.size,
      status: "uploading",
      retries: 0,
    });
    // Always set BOTH endpoint AND (when known) uploadUrl. tus-js-client v4's _resumeUpload
    // silently falls back to _createUpload (POST /tus/) when HEAD returns any 4xx AND
    // endpoint is set (see upload.js:622-636); that fallback hits our server's
    // TusUploadRegistrationService.bindTusUpload, which handles the URL rebind and resets
    // SourceFile.bytesUploaded to 0. Historically we omitted endpoint here so the fallback
    // couldn't fire — the concern was "silent byte-0 restart is a bandwidth burn". Real
    // incident (2026-08-06) reversed the tradeoff: paused uploads whose server-side TUS
    // session was purged (TTL / cleanup / server restart) 404'd on HEAD, flipped to FAILED,
    // and left the user in a 'reload → try to resume → 404 → repeat' loop with no recovery
    // path. The standing rule ('re-upload from byte 0 OK, prefer converging sweepers')
    // permits the byte-0 restart, so we accept it in exchange for breaking the stuck loop.
    // classifyTusError also returns transient for 404/410 (`tus_session_gone`) so if the
    // fallback itself fails, auto-pause fires instead of a terminal FAILED.
    const resolvedUploadUrl = toAbsoluteTusUrl(baseUrl, pair.expected.tusUploadUrl);
    const tusUpload = new tus.Upload(pair.file, {
      endpoint,
      ...(resolvedUploadUrl ? { uploadUrl: resolvedUploadUrl } : {}),
      metadata: {
        packageId: storedRecord.uploadId,
        sourceFileId: pair.expected.sourceFileId,
        filename: pair.file.name,
        contentType: pair.file.type || "application/octet-stream",
      },
      headers: headersRef,
      chunkSize: chunkSizeForConnection(),
      // See the matching comment in upload's startSingle: this scopes tus-js-client's
      // localStorage by (packageId, sourceFileId) so findPreviousUploads below cannot
      // accidentally return a sibling package's TUS URL when the same physical file is
      // present in two parallel uploads.
      fingerprint: makeNamespacedFingerprint(storedRecord.uploadId, pair.expected.sourceFileId),
      // Always 1: `parallelUploads > 1` would invoke the TUS Concatenation extension and
      // split the file into partial uploads whose POSTs strip our custom Upload-Metadata,
      // breaking ownership validation. Throughput comes from per-package file-level
      // parallelism (parallelFilesForConnection) instead.
      parallelUploads: 1,
      retryDelays: TUS_RETRY_DELAYS,
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      onShouldRetry: (err, retryAttempt) => {
        const status = err?.originalResponse?.getStatus?.();
        const responseText = err?.originalResponse?.getBody?.();
        // tus-js-client 4 exposes response headers via getHeader(name); grab the ones that
        // matter for diagnosing a stuck upload — offset + length + resumable version. Empty
        // ones show as null and are still informative (missing Upload-Offset is often what
        // makes tus-js-client 'retry' a 200 response — it can't verify progress).
        const uploadOffset = err?.originalResponse?.getHeader?.('Upload-Offset');
        const uploadLength = err?.originalResponse?.getHeader?.('Upload-Length');
        const tusResumable = err?.originalResponse?.getHeader?.('Tus-Resumable');
        const contentType = err?.originalResponse?.getHeader?.('Content-Type');
        const pkg = perFileProgress.get(storedRecord.uploadId);
        const prev = pkg?.get(pair.expected.sourceFileId);
        updatePerFile(storedRecord.uploadId, pair.expected.sourceFileId, {
          retries: (prev?.retries || 0) + 1,
          status: "retrying",
        });
        const decide = (v, reason) => {
          console.info('[UPLOAD-DIAG] resume/onShouldRetry', {
            sourceFileId: pair.expected.sourceFileId,
            name: pair.expected.originalName,
            attempt: retryAttempt,
            status,
            uploadOffset,
            uploadLength,
            tusResumable,
            contentType,
            responseSnippet: typeof responseText === 'string' ? responseText.slice(0, 200) : null,
            errMsg: err?.message,
            decision: v ? 'RETRY' : 'GIVE_UP',
            reason,
          });
          return v;
        };
        if (status === 401) {
          refreshTokenIfNeeded({ force: true })
            .then((fresh) => { headersRef.Authorization = `Bearer ${fresh}`; })
            .catch(() => {});
          return decide(true, 'refresh-token');
        }
        if (status === 403) return decide(false, 'unrecoverable-4xx');
        // 404 / 410 = server-side TUS session gone. Retry lets tus-js-client's
        // automatic HEAD-4xx → POST-endpoint fallback engage, creating a fresh
        // session and re-uploading from byte 0. See the endpoint/uploadUrl
        // comment on the tus.Upload constructor above for the tradeoff.
        if (status === 404 || status === 410) return decide(retryAttempt < TUS_RETRY_DELAYS.length, 'tus-session-gone-retry-with-fresh-post');
        // 409 = TUS Upload-Offset conflict — retry lets tus-js-client HEAD-resync + resume.
        // See upload() onShouldRetry for the full rationale (2026-08-05).
        if (status === 409) return decide(retryAttempt < TUS_RETRY_DELAYS.length, 'tus-offset-conflict');
        if (status === 423) return decide(false, 'session-locked');
        return decide(retryAttempt < TUS_RETRY_DELAYS.length, `budget=${retryAttempt}/${TUS_RETRY_DELAYS.length}`);
      },
      onProgress: (bytesLoaded) => {
        fileProgress.set(pair.expected.sourceFileId, bytesLoaded);
        updatePerFile(storedRecord.uploadId, pair.expected.sourceFileId, {
          loaded: bytesLoaded,
          status: "uploading",
        });
        watchdog.notifyProgress();
        updateAggregate();
      },
      onSuccess: () => {
        console.info('[UPLOAD-DIAG] resume/onSuccess', {
          sourceFileId: pair.expected.sourceFileId,
          name: pair.expected.originalName,
          sizeBytes: pair.file.size,
        });
        fileProgress.set(pair.expected.sourceFileId, pair.file.size);
        updatePerFile(storedRecord.uploadId, pair.expected.sourceFileId, {
          loaded: pair.file.size,
          status: "uploaded",
        });
        watchdog.stop();
        watchdogsByFile.delete(pair.expected.sourceFileId);
        markUploadedPromises.push(
          markFileUploaded(storedRecord.uploadId, pair.expected.sourceFileId).catch(() => {})
        );
        // See upload() onSuccess: keep the offline/online handlers from re-`.start()`ing
        // an already-completed upload, which restarts it from byte 0.
        tusByFile.delete(pair.expected.sourceFileId);
        updateAggregate();
        resolveOne();
      },
      onError: (err) => {
        const status = err?.originalResponse?.getStatus?.();
        const responseBody = err?.originalResponse?.getBody?.();
        console.info('[UPLOAD-DIAG] resume/onError', {
          sourceFileId: pair.expected.sourceFileId,
          name: pair.expected.originalName,
          status,
          errMsg: err?.message,
          responseSnippet: typeof responseBody === 'string' ? responseBody.slice(0, 300) : null,
        });
        // Symmetric 423 handling with upload() — see the comment there for full rationale.
        if (status === 423) {
          const takenOverError = new Error("This upload was taken over by another device.");
          takenOverError.code = "UPLOAD_TAKEN_OVER";
          takenOverError.userMessage = "Uploading — taken over by another device.";
          takenOverError.uploadId = storedRecord.uploadId;
          updatePerFile(storedRecord.uploadId, pair.expected.sourceFileId, {
            status: "paused",
            error: null,
          });
          watchdog.stop();
          watchdogsByFile.delete(pair.expected.sourceFileId);
          tusByFile.delete(pair.expected.sourceFileId);
          for (const [siblingId, siblingTus] of tusByFile.entries()) {
            if (siblingId === pair.expected.sourceFileId) continue;
            try { siblingTus.abort(false); } catch {}
          }
          rejectOne(takenOverError);
          return;
        }
        // Auto-pause on transient errors — symmetric with upload().
        const classification = classifyTusError(err);
        if (classification.transient) {
          console.info('[UPLOAD-DIAG] resume/auto-pause-on-transient-error', {
            uploadId: storedRecord.uploadId,
            sourceFileId: pair.expected.sourceFileId,
            reason: classification.reason,
            status,
            errMsg: err?.message,
          });
          // See upload() for the rationale: retire this instance, requeue the pair with the
          // TUS URL carried forward, tell Redux via the auto-pause bus.
          const resumeUrl = tusUpload.url || pair.expected.tusUploadUrl || null;
          watchdog.stop();
          watchdogsByFile.delete(pair.expected.sourceFileId);
          tusByFile.delete(pair.expected.sourceFileId);
          try { tusUpload.abort(false); } catch {}
          try { pauseInFlight(storedRecord.uploadId, "auto"); } catch (pauseErr) {
            console.warn('[UPLOAD-DIAG] auto-pause-failed', { uploadId: storedRecord.uploadId, message: pauseErr?.message });
          }
          updatePerFile(storedRecord.uploadId, pair.expected.sourceFileId, {
            status: "paused",
            error: null,
          });
          notifyAutoPause({
            uploadId: storedRecord.uploadId,
            reason: classification.reason,
            message: err?.message || String(err),
          });
          resolveOne(buildAutoPauseSentinel({
            expected: { ...pair.expected, tusUploadUrl: resumeUrl },
            file: pair.file,
          }));
          return;
        }
        updatePerFile(storedRecord.uploadId, pair.expected.sourceFileId, {
          status: "failed",
          error: err?.message || String(err),
        });
        watchdog.stop();
        watchdogsByFile.delete(pair.expected.sourceFileId);
        tusByFile.delete(pair.expected.sourceFileId);
        rejectOne(err);
      },
    });
    tusByFile.set(pair.expected.sourceFileId, tusUpload);
    const watchdog = createStallWatchdog({
      uploadId: storedRecord.uploadId,
      sourceFileId: pair.expected.sourceFileId,
      sessionRecord,
      filename: pair.file.name,
    });
    watchdogsByFile.set(pair.expected.sourceFileId, watchdog);
    watchdog.attach(tusUpload);
    // Prefer resuming if a previous fingerprint exists; otherwise just start. In tus-js-client
    // v4 findPreviousUploads is an instance method (it was static in v1, hence the older code).
    tusUpload
      .findPreviousUploads()
      .then((previousUploads) => {
        console.info('[UPLOAD-DIAG] resume/tus.start', {
          sourceFileId: pair.expected.sourceFileId,
          name: pair.expected.originalName,
          previousUploadsCount: previousUploads?.length || 0,
          usingUploadUrl: pair.expected.tusUploadUrl || null,
        });
        if (previousUploads && previousUploads.length > 0) {
          tusUpload.resumeFromPreviousUpload(previousUploads[0]);
        }
        tusUpload.start();
      })
      .catch((err) => {
        console.info('[UPLOAD-DIAG] resume/tus.findPreviousUploads-failed', {
          sourceFileId: pair.expected.sourceFileId,
          errMsg: err?.message,
        });
        tusUpload.start();
      });
  });

  // Flush the pre-seeded completed bytes into the telemetry/onProgress pipeline before any
  // worker starts. Without this, the progress card would briefly show 0% (then jump up when
  // the first chunk of a pending file lands), even though server-side N files are already
  // done.
  if (completedEntries.length > 0) {
    updateAggregate();
  }

  const maxConcurrentFiles = parallelFilesForConnection();

  // Slot was reserved synchronously at function entry (slotPromise). Just await our turn.
  if (activePackageId !== storedRecord.uploadId) {
    uploadTelemetryService.markWaiting(storedRecord.uploadId);
  }
  await slotPromise;
  if (sessionRecord.cancelled) {
    const aborted = new Error("Upload cancelled while queued");
    aborted.name = "AbortError";
    aborted.code = "CANCELLED_QUEUED";
    stopTokenRefresh();
    stopProgressSnapshot();
    removeFromInFlight();
    throw aborted;
  }

  try {
    const pool = await runFileWorkerPool({
      pairs,
      maxConcurrentFiles,
      runOne,
      sessionRecord,
      tusByFile,
    });
    terminalError = pool.terminalError;
    completedCount = pool.completedCount;

    if (sessionRecord.cancelled && !terminalError) {
      const aborted = new Error("Upload cancelled while paused");
      aborted.name = "AbortError";
      aborted.code = "CANCELLED_QUEUED";
      throw aborted;
    }

    if (terminalError) {
      const { passthrough, error } = decideTerminalErrorDisposition(terminalError, pool.failingFileName);
      if (passthrough) {
        throw error;
      }
      uploadTelemetryService.fail(storedRecord.uploadId);
      throw error;
    }

    if (completedCount !== pairs.length) {
      uploadTelemetryService.fail(storedRecord.uploadId);
      throw new Error(`Expected ${pairs.length} file uploads, completed ${completedCount}`);
    }

    // Same as upload(): release before SSE wait so the queue advances on bytes-done, not on
    // server-side finalization.
    releaseUploadSlot(storedRecord.uploadId);

    uploadTelemetryService.markFinalizing(storedRecord.uploadId);
    // Flush markFileUploaded writes first to avoid the zombie-record race when
    // waitForBackendUploadReady eventually deletes the IndexedDB record. The deletion itself
    // is deferred until the backend reaches a truly-terminal status â€” see upload() above.
    await Promise.all(markUploadedPromises);
    await waitForBackendUploadReady({
      uploadId: storedRecord.uploadId,
      sessionRecord,
      onErrorLogPrefix: "[ChunkedUploadService] resume SSE error",
      onTerminal: removeFromInFlight,
    });

    uploadTelemetryService.complete(storedRecord.uploadId);
    stopTokenRefresh();
    stopProgressSnapshot();
    return {
      uploadId: storedRecord.uploadId,
      packageName: storedRecord.packageName,
      resumed: true,
    };
  } catch (err) {
    stopTokenRefresh();
    stopProgressSnapshot();
    releaseUploadSlot(storedRecord.uploadId);
    removeFromInFlight();
    throw err;
  }
};

// User-initiated pause of an in-flight package. abort(false) leaves the partial upload on the
// server so the next .start() picks up at the last acknowledged byte. Worker loops also hold on
// `resumeGate` so the file queue doesn't keep launching new uploads while the user is paused.
// pauseSource distinguishes a user-initiated pause ("user") from an auto-pause
// fired by tus onError ("auto"). The IDB record persists the source so
// UploadProgressLoadingCard can tell an auto-pause-during-reload apart from a
// deliberate user pause and keep rendering the "Upload interrupted by page
// reload" hint on rehydrate. Default "user" preserves the pre-existing shape
// for the two explicit-click callers.
const pauseInFlight = (uploadId, pauseSource = "user") => {
  const session = inFlight.get(uploadId);
  if (!session || session.paused) {
    // Bug 3 (2026-08): "pause does nothing" on very bad network. Prior to
    // this log, pauseUpload's !ok branch was invisible — no console signal,
    // no observability event, no user toast. Log the specific reason so the
    // diagnostics drawer + support runbook can distinguish "session never
    // spawned" (init still POSTing) from "already paused" (double-click),
    // and record it into the upload's diagnostics timeline for remote
    // triage via the admin diagnostics drawer.
    const reason = !session
      ? "no_session_in_flight"
      : "already_paused";
    // eslint-disable-next-line no-console
    console.warn("[UPLOAD-DIAG] pauseInFlight refused", {
      uploadId,
      reason,
      inFlightKeys: Array.from(inFlight.keys()),
    });
    try {
      const diag = uploadDiagnosticsRegistry.get(uploadId);
      diag?.record("upload_pause_refused", { reason });
    } catch (_error) {
      /* observability must never break pause */
    }
    return false;
  }
  // Pause queued packages too, not just the active one. Earlier this path early-returned for
  // anything where activePackageId !== uploadId, on the reasoning that queued packages aren't
  // burning bytes yet. The side effect was a Pause-All that left queued packages stuck in
  // UPLOADING â€” so the top-bar showed BOTH Pause and Resume buttons after a Pause-All, because
  // hasUploading was still true from the queued one. From the user's POV "Pause All" means
  // "pause everything"; queued packages now flip to PAUSED in Redux, their launchWorker loop
  // honours the resumeGate when its slot eventually opens, and tusByFile being empty makes the
  // abort loop a safe no-op.
  session.paused = true;
  session.resumeGate = new Promise((resolve) => {
    session.resolveResume = resolve;
  });
  for (const t of session.tusByFile.values()) {
    try { t.abort(false); } catch {}
  }
  // Snapshot per-file loaded counts BEFORE marking files paused â€” that way the persisted
  // record carries accurate bytesUploaded values for post-reload rehydration.
  const fileProgressSnapshot = {};
  const pkg = perFileProgress.get(uploadId);
  if (pkg) {
    pkg.forEach((entry, sourceFileId) => {
      fileProgressSnapshot[sourceFileId] = entry.loaded || 0;
      if (entry.status === "uploading" || entry.status === "retrying") {
        updatePerFile(uploadId, sourceFileId, { status: "paused" });
      }
    });
  }
  uploadTelemetryService.pause(uploadId);
  markPackagePaused(uploadId, true, fileProgressSnapshot, pauseSource).catch(() => {});
  // Tell the backend so the DB row flips to PAUSED. Resume is implicit: the next TUS PATCH
  // (when the user clicks Resume) flips it back to CHUNK_UPLOAD_IN_PROGRESS server-side.
  notifyServerPause(uploadId);
  return true;
};

// Boot-time helper: rehydrates the per-file progress map from a persisted record so the card's
// file rows show the correct per-file state immediately after page reload, without waiting for
// the user to re-select files. Files that already completed before the reload are seeded as
// "uploaded" so the user sees the correct partial-progress state ("file 1: Done, file 2: Paused
// at 30%") instead of every file showing as paused regardless of actual completion.
// Trust ONLY the chunkStatus mark, which is written exclusively by markFileUploaded inside
// tus.onSuccess â€” i.e., after the server acknowledged the upload. The previous fallback
// (bytesUploaded >= sizeBytes) treated "client finished sending bytes" as proof of
// completion, but that's racy: tus.onProgress fires when the socket flushes, BEFORE the
// server's PATCH response. A pause-or-reload between the last onProgress and the
// corresponding onSuccess persisted bytesUploaded=sizeBytes to IDB without ever marking
// chunkStatus, and the file then got silently skipped on resume â€” observed end-to-end on
// package 150145 where 9/11 files uploaded successfully and 2 vanished from the work list.
//
// Losing this fallback is safe: if the IDB read-modify-write race lost a real chunkStatus
// mark, the file is included in pendingEntries on resume â†’ tus-js-client's HEAD on the
// existing TUS session returns the actual server offset â†’ resume picks up at the right
// place. Worst case is a no-op HEAD + a small round-trip; best case (the bug above) is
// actually recovering a file we'd have silently dropped.
const isFileAlreadyUploaded = (entry) =>
  entry?.chunkStatus === "UPLOADED";

export const seedPerFileProgress = (uploadId, files) => {
  if (!uploadId || !Array.isArray(files)) return;
  const pkg = ensurePackage(uploadId);
  files.forEach((f) => {
    if (!f?.sourceFileId) return;
    const uploaded = isFileAlreadyUploaded(f);
    pkg.set(f.sourceFileId, {
      sourceFileId: f.sourceFileId,
      originalName: f.originalName,
      sizeBytes: f.sizeBytes,
      loaded: uploaded ? f.sizeBytes : (f.bytesUploaded || 0),
      status: uploaded ? "uploaded" : "paused",
      retries: 0,
    });
  });
  notifyPerFile(uploadId);
};

// True when this tab holds a live session for the package and it is parked on the resume
// gate (user pause or auto-pause). Lets Redux-side callers (resumeAllUploads, the
// /sessions/active reconcile) recognise a resumable upload even if the card's Redux status
// drifted to UPLOADING — the exact state behind the "Resume does nothing" report (2026-09).
export const isPausedInFlight = (uploadId) => {
  const session = inFlight.get(uploadId);
  return !!(session && session.paused && !session.cancelled);
};

const resumeInFlight = (uploadId) => {
  const session = inFlight.get(uploadId);
  if (!session || !session.paused) return false;
  session.paused = false;
  if (session.resolveResume) {
    session.resolveResume();
    session.resolveResume = null;
    session.resumeGate = null;
  }
  for (const t of session.tusByFile.values()) {
    try { t.start(); } catch {}
  }
  const pkg = perFileProgress.get(uploadId);
  if (pkg) {
    pkg.forEach((entry, sourceFileId) => {
      if (entry.status === "paused") {
        updatePerFile(uploadId, sourceFileId, { status: "uploading" });
      }
    });
  }
  uploadTelemetryService.resume(uploadId);
  markPackagePaused(uploadId, false).catch(() => {});
  return true;
};

// Ownership-loss cleanup: another device claimed the upload while our tus loop was still
// running. Tear down the local session's resources (tus workers, queue slot, Web Lock,
// inFlight entry) WITHOUT calling the server cancel endpoint — the row is still alive under
// the new owner and cancelling it would blow up their upload. Called by the /sessions/active
// poll's ownership-lost branch in packagesSlice. Idempotent.
const releaseOwnershipLostSession = (uploadId) => {
  const session = inFlight.get(uploadId);
  if (session) {
    try { session.abort(); } catch {}
    inFlight.delete(uploadId);
    try { session.releaseOwnershipLock?.(); } catch (_) { /* ignore */ }
    session.releaseOwnershipLock = null;
  }
  // Belt-and-braces: session.abort() releases the slot, but if the session was already
  // half-torn-down we still want activePackageId cleared so the next acquire doesn't queue.
  releaseUploadSlot(uploadId);
  releaseReservedSlot(uploadId);
  clearPerFile(uploadId);
};

const ChunkedUploadService = {
  upload,
  cancel,
  resume,
  pauseInFlight,
  resumeInFlight,
  isPausedInFlight,
  releaseOwnershipLostSession,
};

export default ChunkedUploadService;
